import axios from 'axios';
import { Tournament, Player, Pairing, Standing, PlayerInactiveRound, ReplacementPlayer, ReEntryRecord, PairingDrawback } from '../types';
import {
  AuthSource,
  pickAuthToken,
  shouldClearStaffSession,
  shouldRedirectToSignIn,
} from '../lib/apiAuthSource';

// Determine API base URL based on environment
export const getApiBaseUrl = () => {
  // If explicitly set in environment variables, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // In production (Heroku), use relative URLs
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }

  // In development, use full URL to localhost:5000 (backend server port)
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

if (process.env.NODE_ENV !== 'production') {
  console.log('API_BASE_URL:', API_BASE_URL);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
  withCredentials: false,
});

/** Avoid cache-busting on mutations only; GETs can use server Cache-Control when provided. */
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get' && method !== 'head') {
    config.headers = config.headers || {};
    config.headers['Cache-Control'] = 'no-cache';
    config.headers.Pragma = 'no-cache';
  }
  return config;
});

/** The player session's token, or null when nobody is signed in as a member. */
const readPlayerToken = (): string | null => {
  try {
    const stored = localStorage.getItem('paircraft_player_auth');
    const parsed = stored ? JSON.parse(stored) : null;
    return parsed?.token || null;
  } catch {
    return null;
  }
};

// Add request interceptor for authentication and debugging
api.interceptors.request.use(
  (config) => {
    // Which session is speaking. The staff session (a ChessDirector account)
    // and the player session (a club member) are independent: a request
    // records the one it used so a failure only ever affects that session,
    // and staff-only endpoints never borrow a member's token.
    if (config.headers?.Authorization) {
      (config as any).__authSource = 'explicit' as AuthSource;
      return config;
    }

    const { token, source } = pickAuthToken(config.url, {
      staffToken: localStorage.getItem('token'),
      playerToken: readPlayerToken(),
    });
    (config as any).__authSource = source;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for authentication error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Log detailed error information
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      code: error.code,
      timeout: error.code === 'ECONNABORTED',
      networkError: !error.response,
      responseData: error.response?.data,
      requestData: error.config?.data,
      headers: error.response?.headers,
      timestamp: new Date().toISOString()
    };

    console.error('❌ API Response Error:', errorDetails);

    // Log request payload if available
    if (error.config?.data) {
      try {
        const requestPayload = typeof error.config.data === 'string'
          ? JSON.parse(error.config.data)
          : error.config.data;
        console.error('📤 Request payload that failed:', requestPayload);
      } catch (e) {
        console.error('📤 Request payload (raw):', error.config.data);
      }
    }

    // Log response body if available
    if (error.response?.data) {
      console.error('📥 Server response body:', error.response.data);
    } else {
      console.error('⚠️ No response data received from server');
    }

    // Handle network errors with retry logic
    if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response) {
      console.log('Network error detected, attempting retry...');

      // Retry logic for network errors
      const config = error.config;
      if (!config || config.__retryCount >= 3) {
        return Promise.reject(error);
      }

      // Initialize retry count if not exists
      if (!config.__retryCount) {
        config.__retryCount = 0;
      }

      config.__retryCount++;
      console.log(`Retrying request (${config.__retryCount}/3)...`);

      // Wait before retry with exponential backoff
      const delay = 1000 * Math.pow(2, config.__retryCount - 1);
      await new Promise(resolve => setTimeout(resolve, delay));

      return api(config);
    }

    // Handle authentication errors — clear the credentials that actually
    // failed, and nothing else. A member's expired token must not sign a
    // director out of the staff session, or the other way about.
    if (error.response?.status === 401) {
      const source: AuthSource = (error.config as any)?.__authSource || 'none';
      if (shouldClearStaffSession(source)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (shouldRedirectToSignIn(source, window.location.pathname)) {
          window.location.href = '/sign-in';
        }
      }
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.status, error.response.data);
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (credentials: any) => api.post('/auth/login', credentials),
  register: (userData: any) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (userData: any) => api.put('/auth/profile', userData),
  changePassword: (passwordData: any) => api.put('/auth/change-password', passwordData),
  logout: () => api.post('/auth/logout'),
};



// Tournament API
export const tournamentApi = {
  getAll: () => api.get<{ success: boolean, data: Tournament[], error?: string }>(`/tournaments`),
  /**
   * Every tournament one club owns, unpublished ones included — what an admin
   * listing needs and `search` cannot give, since it only ever returns
   * `is_public = 1`.
   */
  listByOrganization: (organizationId: string) =>
    api.get<{ success: boolean, data: Tournament[], error?: string }>(
      `/tournaments?organization_id=${encodeURIComponent(organizationId)}`
    ),
  search: (params?: {
    q?: string;
    organization_id?: string;
    status?: string;
    format?: string;
    city?: string;
    state?: string;
    start_date_from?: string;
    start_date_to?: string;
    fide_rated?: boolean;
    uscf_rated?: boolean;
    /** Leave out league parents — containers for other events, not events. */
    exclude_leagues?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.q) searchParams.append('q', params.q);
    if (params?.organization_id) searchParams.append('organization_id', params.organization_id);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.format) searchParams.append('format', params.format);
    if (params?.city) searchParams.append('city', params.city);
    if (params?.state) searchParams.append('state', params.state);
    if (params?.start_date_from) searchParams.append('start_date_from', params.start_date_from);
    if (params?.start_date_to) searchParams.append('start_date_to', params.start_date_to);
    if (params?.fide_rated) searchParams.append('fide_rated', 'true');
    if (params?.uscf_rated) searchParams.append('uscf_rated', 'true');
    if (params?.exclude_leagues) searchParams.append('exclude_leagues', 'true');
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    return api.get<{ success: boolean, data: { tournaments: Tournament[], total: number, limit: number, offset: number }, error?: string }>(`/tournaments/search?${searchParams.toString()}`);
  },
  getById: (id: string) => api.get<{ success: boolean, data: Tournament, error?: string }>(`/tournaments/${id}`),
  getPublic: (id: string, options?: { lite?: boolean }) =>
    api.get<{ success: boolean; data: any; error?: string }>(`/tournaments/${id}/public`, {
      params: options?.lite ? { lite: '1' } : undefined,
    }),

  getPublicPrizes: (id: string) =>
    api.get<{
      success: boolean;
      data: { prizes?: any[]; prizeDistributions?: any[]; teamPrizeDistributions?: any[] };
      error?: string;
    }>(`/tournaments/${id}/public/prizes`),
  submitContactMessage: (
    id: string,
    payload: { name: string; email: string; phone?: string; message: string; metadata?: any }
  ) =>
    api.post<{ success: boolean; data: any; error?: string }>(
      `/tournaments/${id}/contact-messages`,
      payload
    ),
  getContactMessages: (id: string, limit?: number) =>
    api.get<{ success: boolean; data: any[]; error?: string }>(
      `/tournaments/${id}/contact-messages`,
      limit ? { params: { limit } } : undefined
    ),
  // Announcements
  getAnnouncements: (id: string) =>
    api.get<{ success: boolean, data: { announcements: any[] }, error?: string }>(`/tournaments/${id}/announcements/all`),
  createAnnouncement: (id: string, announcement: { title: string, content: string, isPublished?: boolean, expiresAt?: string }) =>
    api.post<{ success: boolean, message: string, data: { announcementId: string }, error?: string }>(`/tournaments/${id}/announcements`, announcement),
  updateAnnouncement: (id: string, announcementId: string, announcement: { title?: string, content?: string, isPublished?: boolean, expiresAt?: string }) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/tournaments/${id}/announcements/${announcementId}`, announcement),
  deleteAnnouncement: (id: string, announcementId: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/tournaments/${id}/announcements/${announcementId}`),
  create: (tournament: Omit<Tournament, 'id' | 'created_at'>) =>
    api.post<{ success: boolean, data: { id: string }, error?: string }>('/tournaments', tournament),
  update: (id: string, tournament: Partial<Tournament>) =>
    api.put(`/tournaments/${id}`, tournament),
  delete: (id: string) => api.delete(`/tournaments/${id}`),
  /** Download full tournament data (all rounds, players, pairings, etc.) as JSON file */
  downloadFullData: (id: string) =>
    api.get(`/tournaments/${id}/download`, { responseType: 'blob' }),
  /** Download portable tournament results snapshot JSON */
  downloadResultsSnapshot: (id: string, allowIncomplete: boolean = false) =>
    api.get(`/tournaments/${id}/results-snapshot?allowIncomplete=${allowIncomplete ? 'true' : 'false'}`, { responseType: 'blob' }),
  /** Import tournament from previously exported JSON; returns new tournament ID */
  importTournament: (data: { tournament: any; players?: any[]; pairings?: any[]; results?: any[]; inactiveRounds?: any[]; teams?: any[]; teamMembers?: any[]; teamResults?: any[] }, organizationId?: string) =>
    api.post<{ success: boolean, data: { tournamentId: string, counts: any }, message?: string, error?: string }>('/tournaments/import', { data, organizationId }),
  completeTournament: (id: string) =>
    api.post<{ success: boolean, message: string, prizeResult?: any, error?: string }>(`/tournaments/${id}/complete`),
  // Prize management
  getSections: (id: string) => api.get<{ success: boolean, data: string[], error?: string }>(`/tournaments/${id}/sections`),
  getPrizes: (id: string) => api.get<{ success: boolean, data: any[], error?: string }>(`/tournaments/${id}/prizes`),
  getWinners: (id: string) =>
    api.get<{
      success: boolean;
      data: {
        sections: any[];
        totals: any;
        lastUpdated?: string;
      };
      error?: string;
    }>(`/tournaments/${id}/winners`),
  calculatePrizes: (id: string) => api.post<{ success: boolean, data: any[], message?: string, error?: string, metadata?: any }>(`/tournaments/${id}/prizes/calculate`),
  updatePrizeSettings: (id: string, prizeSettings: any) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/tournaments/${id}/prize-settings`, { prizeSettings }),
  generatePrizeStructure: (id: string, prizeFund?: number) =>
    api.post<{ success: boolean, data: any, message?: string, error?: string }>(`/tournaments/${id}/generate-prize-structure`, { prizeFund }),
  assignPrize: (id: string, prizeData: { playerId: string, prizeName: string, prizeType: string, amount?: number, position?: number, section?: string, ratingCategory?: string }) =>
    api.post<{ success: boolean, message: string, data?: any, error?: string }>(`/tournaments/${id}/prizes/assign`, prizeData),
  removePrizeAssignment: (id: string, distributionId: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/tournaments/${id}/prizes/assign/${distributionId}`),
  assignTeamPrize: (id: string, prizeData: { teamId?: string, teamIdentifier?: string, teamName: string, prizeName: string, prizeType: string, amount?: number, position?: number, section?: string }) =>
    api.post<{ success: boolean, message: string, data?: any, error?: string }>(`/tournaments/${id}/team-prizes/assign`, prizeData),
  removeTeamPrizeAssignment: (id: string, distributionId: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/tournaments/${id}/team-prizes/assign/${distributionId}`),
  getSpecialPrizes: (id: string, section?: string | null) =>
    api.get<{ success: boolean, prizes: any[], sections: string[], error?: string }>(
      `/pairings/tournament/${id}/prizes/special${section ? `?section=${encodeURIComponent(section)}` : ''}`
    ),
  // Section management
  mergeSections: (id: string, sourceSection: string, targetSection: string, removeSourceSection?: boolean) =>
    api.post<{
      success: boolean, message: string, data: {
        playersUpdated?: number;
        pairingsUpdated?: number;
        registrationsUpdated?: number;
        teamsUpdated?: number;
        prizesUpdated?: number;
        prizeDistributionsUpdated?: number;
        sourceSectionRemoved?: boolean;
      }, error?: string
    }>(
      `/tournaments/${id}/merge-sections`,
      { sourceSection, targetSection, removeSourceSection: removeSourceSection ?? false }
    ),
  mergeSectionsToNew: (id: string, payload: any) =>
    api.post<{ success: boolean; message: string; data?: any; error?: string }>(
      `/tournaments/${id}/merge-sections-bulk`,
      payload
    ),
  // Team-related endpoints
  getTeamStandings: (id: string, params?: { type?: string, scoring_method?: string, top_n?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.scoring_method) queryParams.append('scoring_method', params.scoring_method);
    if (params?.top_n) queryParams.append('top_n', params.top_n.toString());

    return api.get<{ success: boolean, standings: any[], standingsBySection?: { [key: string]: any[] }, type: string, scoring_method: string, top_n?: number, error?: string }>(`/teams/tournament/${id}/standings?${queryParams.toString()}`);
  },
  getTeamPairings: (id: string, round: number) =>
    api.get<{ success: boolean, data: any[], error?: string }>(`/teams/tournament/${id}/round/${round}/results`),
  getTeamMatchResults: (id: string, round: number) =>
    api.get<{ success: boolean, results: any[], error?: string }>(`/teams/tournament/${id}/round/${round}/match-results`),
  getPlayerPerformance: (tournamentId: string, playerId: string) =>
    api.get<{ success: boolean, tournament: any, player: any, roundPerformance: any[], positionHistory: any[], standings: any[], statistics: any, error?: string }>(`/tournaments/${tournamentId}/player/${playerId}/performance`),
  // Branded PDF exports
  downloadScoreSheets: (id: string, round: number) => {
    return api.get(`/tournaments/${id}/score-sheets?round=${round}`, {
      responseType: 'blob'
    }).then(response => {
      // Check if response is actually an error (JSON error returned as blob)
      const contentType = response.headers['content-type'] || '';
      if (response.status >= 400 || !contentType.includes('application/pdf')) {
        // Try to parse as JSON error
        return response.data.text().then((text: string) => {
          try {
            const parsed = JSON.parse(text);
            throw new Error(parsed.error || parsed.message || `Failed to download score sheets: ${response.statusText}`);
          } catch (parseError) {
            // If it's not JSON, it might be HTML error page or other error format
            if (response.status >= 400) {
              throw new Error(`Failed to download score sheets: ${response.statusText || 'Server error'}`);
            }
            // If status is OK but content-type is wrong, still try to download
            // (some servers might not set content-type correctly)
          }
        });
      }

      // Normal blob handling for PDF
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Round_${round}_Score_Sheets.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return response;
    }).catch(error => {
      // Handle axios errors
      if (error.response) {
        // Server responded with error status
        const contentType = error.response.headers['content-type'] || '';
        if (contentType.includes('application/json')) {
          // JSON error response
          throw new Error(error.response.data.error || error.response.data.message || 'Failed to download score sheets');
        } else if (error.response.data instanceof Blob) {
          // Blob error response (shouldn't happen but handle it)
          return error.response.data.text().then((text: string) => {
            try {
              const parsed = JSON.parse(text);
              throw new Error(parsed.error || parsed.message || 'Failed to download score sheets');
            } catch {
              throw new Error(`Failed to download score sheets: ${error.response.statusText || 'Server error'}`);
            }
          });
        }
      }
      throw error;
    });
  },
  downloadBlankScoreSheets: (id: string, round: number) => {
    return api.get(`/tournaments/${id}/score-sheets/template?round=${round}`, {
      responseType: 'blob'
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `Round_${round}_Blank_Score_Sheets.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return response;
    });
  },
  generateBoardNumbersPDF: (id: string, options: {
    startingBoardNumber: number;
    endingBoardNumber?: number | null;
    textRows: string[];
    logoUrl?: string | null;
  }) => {
    return api.post(`/tournaments/${id}/board-numbers-pdf`, options, {
      responseType: 'blob'
    });
  },
  downloadQuadForms: (id: string, round: number) => {
    return api.get(`/tournaments/${id}/quad-forms?round=${round}`, {
      responseType: 'blob'
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Round_${round}_Quad_Forms.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return response;
    });
  },
  // Get bracket data for elimination tournaments (convenience method)
  getBracket: (tournamentId: string, section?: string) =>
    pairingApi.getBracket(tournamentId, section),

  // Get bracket data for team elimination tournaments
  getTeamBracket: (tournamentId: string) =>
    pairingApi.getTeamBracket(tournamentId),
};

// Replacement Players API
export const replacementApi = {
  list: (tournamentId: string, status?: string) => {
    const params = new URLSearchParams();
    if (status) {
      params.append('status', status);
    }
    const qs = params.toString();
    return api.get<{ success: boolean; data: ReplacementPlayer[]; error?: string }>(
      `/replacements/tournament/${tournamentId}${qs ? `?${qs}` : ''}`
    );
  },
  create: (payload: Partial<ReplacementPlayer> & { tournament_id: string }) =>
    api.post<{ success: boolean; data: ReplacementPlayer; message?: string; error?: string }>(
      '/replacements',
      payload
    ),
  update: (id: string, payload: Partial<ReplacementPlayer>) =>
    api.put<{ success: boolean; data: ReplacementPlayer; message?: string; error?: string }>(
      `/replacements/${id}`,
      payload
    ),
  remove: (id: string) =>
    api.delete<{ success: boolean; message?: string; error?: string }>(`/replacements/${id}`)
};

// Club Members API
export const clubMembersApi = {
  getAll: (organizationId: string, params?: { status?: string, search?: string, role?: string, coachId?: string, unassigned?: boolean }) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.coachId) queryParams.append('coachId', params.coachId);
    if (params?.unassigned) queryParams.append('unassigned', 'true');
    return api.get<{ success: boolean, data: { members: any[], access?: { isAdmin: boolean, coachMemberId: string | null } }, error?: string }>(`/club-members?${queryParams.toString()}`);
  },
  getRosterContext: (organizationId: string) =>
    api.get<{
      success: boolean;
      data: {
        isAdmin: boolean;
        coachMemberId: string | null;
        coaches: Array<{ id: string; name: string; email?: string; rating?: number }>;
        summary: {
          totalStudents: number;
          unassigned: number;
          myRosterCount: number;
          byCoach: Array<{ coachId: string; coachName: string; count: number }>;
        };
      };
      error?: string;
    }>(`/club-members/roster-context?organizationId=${organizationId}`),
  bulkAssignCoach: (data: { organizationId: string; memberIds: string[]; coachId?: string | null }) =>
    api.post<{ success: boolean, message: string, data: { updated: number }, error?: string }>('/club-members/bulk-assign-coach', data),
  getById: (id: string) =>
    api.get<{ success: boolean, data: { member: any }, error?: string }>(`/club-members/${id}`),
  create: (member: any) =>
    api.post<{ success: boolean, data: { member: any }, error?: string }>('/club-members', member),
  update: (id: string, member: Partial<any>) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/club-members/${id}`, member),
  delete: (id: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/club-members/${id}`),
  bulkCreate: (organizationId: string, members: any[]) =>
    api.post<{ success: boolean, message: string, data: { created: any[], errors?: any[] }, error?: string }>('/club-members/bulk', { organizationId, members }),
  importToTournament: (organizationId: string, tournamentId: string, memberIds: string[], section?: string) =>
    api.post<{ success: boolean, message: string, data: { imported: any[], skipped?: any[] }, error?: string }>('/club-members/import-to-tournament', { organizationId, tournamentId, memberIds, section }),
  importFromTournament: (organizationId: string, tournamentId: string) =>
    api.post<{ success: boolean, message: string, data: { imported: any[], skipped?: any[], errors?: any[] }, error?: string }>('/club-members/import-from-tournament', { organizationId, tournamentId }),
  exportToMailingList: (organizationId: string, memberIds?: string[]) =>
    api.post<{ success: boolean, message: string, data: { exported: number, skipped: number, errors: number, errorDetails?: any[] }, error?: string }>('/club-members/export-to-mailing-list', { organizationId, memberIds }),
  // Coach / student management
  getCoaches: (organizationId: string) =>
    api.get<{ success: boolean, data: { coaches: any[] }, error?: string }>(`/club-members/coaches?organizationId=${organizationId}`),
  getCoachStudents: (organizationId: string, coachId: string) =>
    api.get<{ success: boolean, data: { students: any[] }, error?: string }>(`/club-members/coaches/${coachId}/students?organizationId=${organizationId}`),
  /** Self-register as a student on the current tenant club site */
  joinClub: (data?: { name?: string; email?: string; uscfId?: string }) =>
    api.post<{
      success: boolean;
      message: string;
      data: {
        memberId: string;
        memberRole: string;
        organizationId?: string;
        organizationName?: string;
        alreadyMember?: boolean;
      };
      error?: string;
    }>('/tenant/register-student', data || {}),
  /** Join a specific organization by id (used off-tenant, e.g. the player billing tab) */
  joinOrganization: (organizationId: string, data?: { name?: string; email?: string; uscfId?: string }) =>
    api.post<{
      success: boolean;
      message: string;
      data: {
        memberId: string;
        memberRole: string;
        organizationId?: string;
        organizationName?: string;
        alreadyMember?: boolean;
      };
      error?: string;
    }>('/club-members/join', { ...(data || {}), organizationId }),
  /** Organizations that currently allow players to self-join (off-tenant picker) */
  getJoinableOrganizations: (search?: string) =>
    api.get<{ success: boolean, data: { organizations: Array<{ id: string; name: string; slug: string | null; logoUrl: string | null; allowSelfJoin: boolean }> }, error?: string }>(
      `/club-members/joinable-organizations${search ? `?search=${encodeURIComponent(search)}` : ''}`
    ),
  exportCsv: (organizationId: string) =>
    api.get(`/club-members/export-csv?organizationId=${organizationId}`, { responseType: 'blob' }),
  getJoinConfig: (organizationId: string) =>
    api.get<{ success: boolean, data: { questions: Array<{ id: string; label: string; required?: boolean }>, autoApprove: boolean, allowSelfJoin?: boolean }, error?: string }>(`/club-members/join-config?organizationId=${organizationId}`),
  updateJoinConfig: (data: { organizationId: string; questions: Array<{ id?: string; label: string; required?: boolean }>; autoApprove: boolean; allowSelfJoin?: boolean }) =>
    api.put<{ success: boolean, message: string, data: { questions: any[], autoApprove: boolean, allowSelfJoin?: boolean }, error?: string }>('/club-members/join-config', data),
  // Join requests
  submitJoinRequest: (data: { organizationId: string; name: string; email?: string; uscfId?: string; message?: string; requestedRole?: 'student' | 'member'; answers?: Record<string, string> }) =>
    api.post<{ success: boolean, message: string, data: { requestId: string, status: string, memberId?: string }, error?: string }>('/club-members/join-request', data),
  getJoinRequests: (organizationId: string, status?: string) => {
    const params = new URLSearchParams({ organizationId });
    if (status) params.append('status', status);
    return api.get<{ success: boolean, data: { requests: any[] }, error?: string }>(`/club-members/join-requests?${params.toString()}`);
  },
  reviewJoinRequest: (id: string, action: 'approve' | 'reject', reviewNote?: string) =>
    api.put<{ success: boolean, message: string, data: { status: string, memberId?: string }, error?: string }>(`/club-members/join-requests/${id}`, { action, reviewNote }),
};

// Club Features API
export const clubFeaturesApi = {
  sendCustomEmail: (data: {
    organization_id?: string;
    tournament_id?: string;
    recipient_email: string;
    recipient_name: string;
    subject: string;
    html_content: string;
    tracking_id?: string;
    email_type?: 'custom' | 'winner' | 'pairing' | 'uscf_expired' | 'uscf_expiring' | 'uscf_missing' | 'registration' | 'announcement';
  }) => api.post('/club-features/send-custom-email', data),
  sendBulkEmail: (data: {
    organization_id?: string;
    tournament_id?: string;
    recipients: Array<{ email: string; name?: string }>;
    subject: string;
    html_content: string;
    email_type?: string;
  }) => api.post('/club-features/send-bulk-email', data),
  getTournamentEmails: (tournamentId: string) => {
    const queryParams = new URLSearchParams();
    return api.get<{ success: boolean, data: any[] }>(`/club-features/email-tracking/tournament/${tournamentId}?${queryParams.toString()}`);
  },
  getEmailTracking: (trackingId: string) => {
    const queryParams = new URLSearchParams();
    return api.get<{ success: boolean, data: any }>(`/club-features/email-tracking/${trackingId}?${queryParams.toString()}`);
  },
  // Announcements
  getAnnouncements: (organizationId: string, published?: boolean) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (published !== undefined) queryParams.append('published', published.toString());
    return api.get<{ success: boolean, data: { announcements: any[] }, error?: string }>(`/club-features/announcements?${queryParams.toString()}`);
  },
  createAnnouncement: (announcement: any) =>
    api.post<{ success: boolean, message: string, data: { announcementId: string }, error?: string }>('/club-features/announcements', announcement),
  updateAnnouncement: (id: string, announcement: Partial<any>) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/club-features/announcements/${id}`, announcement),
  deleteAnnouncement: (id: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/club-features/announcements/${id}`),

  // Email Campaigns
  getEmailCampaigns: (organizationId: string) => {
    const queryParams = new URLSearchParams({ organizationId });
    return api.get<{ success: boolean, data: { campaigns: any[] }, error?: string }>(`/club-features/email-campaigns?${queryParams.toString()}`);
  },
  createEmailCampaign: (campaign: any) =>
    api.post<{ success: boolean, message: string, data: { campaignId: string }, error?: string }>('/club-features/email-campaigns', campaign),
  sendEmailCampaign: (id: string) =>
    api.post<{ success: boolean, message: string, error?: string }>(`/club-features/email-campaigns/${id}/send`),
  getEmailInsights: (id: string) =>
    api.get<{ success: boolean, data: { campaign: any, metrics: any, tracking: any[] }, error?: string }>(`/club-features/email-campaigns/${id}/insights`),

  // Club Ratings
  getRatings: (organizationId: string, ratingType?: string, limit?: number) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (ratingType) queryParams.append('ratingType', ratingType);
    if (limit) queryParams.append('limit', limit.toString());
    return api.get<{ success: boolean, data: { leaderboard: any[] }, error?: string }>(`/club-features/ratings?${queryParams.toString()}`);
  },
  getMemberRating: (memberId: string, organizationId: string, ratingType?: string) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (ratingType) queryParams.append('ratingType', ratingType);
    return api.get<{ success: boolean, data: { rating: any, history: any[] }, error?: string }>(`/club-features/ratings/${memberId}?${queryParams.toString()}`);
  },
  generateRatingsFromTournament: (organizationId: string, tournamentId: string, ratingType?: string) =>
    api.post<{ success: boolean, message: string, data: any, error?: string }>('/club-features/ratings/generate', { organizationId, tournamentId, ratingType }),

  // Online Game Emails
  sendOnlineGameEmails: (data: {
    pairing_ids?: string[];
    game_ids?: string[];
    round?: number;
    tournament_id?: string;
    section?: string;
  }) => api.post<{ success: boolean, message: string, data: any, error?: string }>('/club-features/online-games/send-email', data),

  // Campaign send status (for polling) + club analytics
  getCampaignStatus: (id: string) =>
    api.get<{ success: boolean, data: { id: string, status: string, errorMessage: string | null, totalRecipients: number, sent: number, delivered: number, opened: number, clicked: number, bounced: number, failed: number }, error?: string }>(`/club-features/email-campaigns/${id}/status`),
  getClubAnalytics: (organizationId: string) =>
    api.get<{ success: boolean, data: any, error?: string }>(`/club-features/analytics?organizationId=${organizationId}`),
};

// Coaching API (lesson notes, homework, messages, progress, bookings)
export const coachingApi = {
  // Lesson notes
  getNotes: (organizationId: string, params?: { studentId?: string; sessionId?: string }) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (params?.studentId) queryParams.append('studentId', params.studentId);
    if (params?.sessionId) queryParams.append('sessionId', params.sessionId);
    return api.get<{ success: boolean, data: { notes: any[] }, error?: string }>(`/coaching/notes?${queryParams.toString()}`);
  },
  getMyNotes: (organizationId: string) =>
    api.get<{ success: boolean, data: { notes: any[] }, error?: string }>(
      `/coaching/notes/mine?organizationId=${organizationId}&_=${Date.now()}`
    ),
  createNote: (data: { organizationId: string; studentId?: string; sessionId?: string; title?: string; content: string; visibility?: 'coach_only' | 'student' }) =>
    api.post<{ success: boolean, message: string, data: { id: string }, error?: string }>('/coaching/notes', data),
  updateNote: (id: string, data: { title?: string; content?: string; visibility?: string }) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/coaching/notes/${id}`, data),
  deleteNote: (id: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/coaching/notes/${id}`),

  // Homework submissions
  submitHomework: (data: { organizationId: string; type?: 'game' | 'position' | 'text'; title: string; content: string; coachMemberId?: string; sessionId?: string }) =>
    api.post<{ success: boolean, message: string, data: { id: string }, error?: string }>('/coaching/homework', data),
  getHomework: (organizationId: string, status?: string) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (status) queryParams.append('status', status);
    return api.get<{ success: boolean, data: { submissions: any[] }, error?: string }>(`/coaching/homework?${queryParams.toString()}`);
  },
  getMyHomework: (organizationId: string) =>
    api.get<{ success: boolean, data: { submissions: any[] }, error?: string }>(
      `/coaching/homework/mine?organizationId=${organizationId}&_=${Date.now()}`
    ),
  reviewHomework: (id: string, feedback: string) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/coaching/homework/${id}/review`, { feedback }),
  deleteHomework: (id: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/coaching/homework/${id}`),
  assignHomework: (data: {
    organizationId: string;
    studentId: string;
    type?: 'text' | 'game' | 'position' | 'commentary' | 'tactics' | 'study' | 'pack';
    title?: string;
    prompt?: string;
    content?: string;
    pgn?: string;
    fen?: string;
    gameUrl?: string;
    source?: string;
    sessionId?: string;
    assignmentPayload?: import('../types/homeworkBoard').HomeworkAssignmentPayload;
    dueDate?: string;
  }) =>
    api.post<{ success: boolean, message: string, data: { id: string }, error?: string }>('/coaching/homework/assign', data),

  getHomeworkBoard: (homeworkId: string) =>
    api.get<{ success: boolean, data: { homework: any; role: 'student' | 'coach'; canSubmit: boolean }, error?: string }>(
      `/coaching/homework/${homeworkId}/board`
    ),

  submitHomeworkBoard: (homeworkId: string, responsePayload: import('../types/homeworkBoard').HomeworkResponsePayload) =>
    api.put<{ success: boolean, message: string, data: { id: string }, error?: string }>(
      `/coaching/homework/${homeworkId}/submit-board`,
      { responsePayload }
    ),

  getHomeworkAssignments: (organizationId: string, status?: string) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (status) queryParams.append('status', status);
    return api.get<{ success: boolean, data: { assignments: any[] }, error?: string }>(
      `/coaching/homework/assignments?${queryParams.toString()}`
    );
  },

  // Messages
  getMessageThreads: (organizationId: string) =>
    api.get<{ success: boolean, data: { threads: any[], myMemberIds: string[] }, error?: string }>(`/coaching/messages/threads?organizationId=${organizationId}`),
  getMessages: (organizationId: string, withMemberId: string) =>
    api.get<{ success: boolean, data: { messages: any[] }, error?: string }>(`/coaching/messages?organizationId=${organizationId}&withMemberId=${withMemberId}`),
  sendMessage: (data: { organizationId: string; recipientMemberId: string; body: string }) =>
    api.post<{ success: boolean, message: string, data: { id: string }, error?: string }>('/coaching/messages', data),

  // Student progress
  getStudentProgress: (organizationId: string, memberId: string) =>
    api.get<{ success: boolean, data: any, error?: string }>(`/coaching/students/${memberId}/progress?organizationId=${organizationId}`),
  getStudentRecentGames: (organizationId: string, memberId: string, limit = 10) =>
    api.get<{ success: boolean, data: { games: any[], linkedAccounts: any }, error?: string }>(
      `/coaching/students/${memberId}/recent-games?organizationId=${organizationId}&limit=${limit}`
    ),
  getMyProgress: (organizationId: string) =>
    api.get<{ success: boolean, data: any, error?: string }>(
      `/coaching/my-progress?organizationId=${organizationId}&_=${Date.now()}`
    ),
  getMyClubs: () =>
    api.get<{ success: boolean, data: { clubs: Array<{ memberId: string; organizationId: string; organizationName: string; memberName?: string; memberRole?: string }> }, error?: string }>(
      `/coaching/my-clubs?_=${Date.now()}`
    ),
  getMyCoachClubs: () =>
    api.get<{ success: boolean, data: { clubs: Array<{ memberId: string; organizationId: string; organizationName: string; memberName?: string; organizationSlug?: string }> }, error?: string }>(
      `/coaching/my-coach-clubs?_=${Date.now()}`
    ),
  getMyCoachSchedule: (organizationId?: string) => {
    const queryParams = new URLSearchParams({ _: String(Date.now()) });
    if (organizationId) queryParams.set('organizationId', organizationId);
    return api.get<{
      success: boolean,
      data: {
        items: Array<{
          type: 'lesson' | 'session' | 'group';
          id: string;
          organizationId: string;
          organizationName?: string;
          title: string;
          studentName?: string;
          startAt: string;
          durationMinutes?: number;
          status: string;
          roomCode?: string;
          notes?: string;
          signupCount?: number;
          capacity?: number;
        }>;
        pendingCount: number;
        organizations: Array<{ id: string; name: string }>;
      },
      error?: string,
    }>(`/coaching/my-schedule?${queryParams.toString()}`);
  },

  // Availability & bookings
  getAvailability: (organizationId: string, coachId?: string) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (coachId) queryParams.append('coachId', coachId);
    return api.get<{ success: boolean, data: { slots: any[] }, error?: string }>(`/coaching/availability?${queryParams.toString()}`);
  },
  setAvailability: (data: { organizationId: string; coachId: string; slots: Array<{ dayOfWeek: number; startTime: string; endTime: string; slotMinutes?: number; rateCents?: number }> }) =>
    api.put<{ success: boolean, message: string, error?: string }>('/coaching/availability', data),
  createBooking: (data: {
    organizationId: string;
    coachId: string;
    startDate: string;
    startTime: string;
    startAt?: string;
    durationMinutes?: 15 | 30 | 60;
    notes?: string;
  }) =>
    api.post<{ success: boolean, message: string, data: { id: string }, error?: string }>('/coaching/bookings', data),
  getBookings: (organizationId: string, params?: { status?: string; coachId?: string }) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (params?.status) queryParams.append('status', params.status);
    if (params?.coachId) queryParams.append('coachId', params.coachId);
    return api.get<{ success: boolean, data: { bookings: any[] }, error?: string }>(`/coaching/bookings?${queryParams.toString()}`);
  },
  getMyBookings: (organizationId: string) =>
    api.get<{ success: boolean, data: { bookings: any[] }, error?: string }>(`/coaching/bookings/mine?organizationId=${organizationId}`),
  updateBooking: (id: string, data: { action?: 'confirm' | 'decline' | 'cancel' | 'complete'; paymentStatus?: 'unpaid' | 'paid' | 'waived'; paymentReference?: string }) =>
    api.put<{ success: boolean, message: string, data?: { session?: { sessionId: string; roomCode: string; boardPath: string } }, error?: string }>(`/coaching/bookings/${id}`, data),

  // Group lesson classes
  getGroupClasses: (organizationId: string, params?: { status?: string }) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (params?.status) queryParams.append('status', params.status);
    return api.get<{ success: boolean, data: { classes: any[] }, error?: string }>(`/coaching/group-classes?${queryParams.toString()}`);
  },
  createGroupClass: (data: {
    organizationId: string;
    coachId?: string;
    title: string;
    description?: string;
    startAt: string;
    durationMinutes?: number;
    capacity?: number;
    ratingMin?: number;
    ratingMax?: number;
  }) => api.post<{ success: boolean, message: string, data: { id: string; sessionId: string; roomCode: string; boardPath: string }, error?: string }>('/coaching/group-classes', data),
  updateGroupClass: (id: string, data: { action: 'cancel' | 'complete' }) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/coaching/group-classes/${id}`, data),
  signupGroupClass: (id: string) =>
    api.post<{ success: boolean, message: string, data: { id: string }, error?: string }>(`/coaching/group-classes/${id}/signup`),
  cancelGroupClassSignup: (id: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/coaching/group-classes/${id}/signup`),
  getGroupClassSignups: (id: string) =>
    api.get<{ success: boolean, data: { signups: any[] }, error?: string }>(`/coaching/group-classes/${id}/signups`),

  // Public coach directory + progress report emails
  getPublicCoaches: (params: { organizationId?: string; organizationSlug?: string }) => {
    const queryParams = new URLSearchParams();
    if (params.organizationId) queryParams.append('organizationId', params.organizationId);
    if (params.organizationSlug) queryParams.append('organizationSlug', params.organizationSlug);
    return api.get<{ success: boolean, data: { coaches: any[] }, error?: string }>(`/coaching/public/coaches?${queryParams.toString()}`);
  },
  sendProgressReports: (organizationId: string, studentIds?: string[]) =>
    api.post<{ success: boolean, message: string, data: { emailed: number, skipped: number }, error?: string }>('/coaching/progress-reports/send', { organizationId, studentIds }),
  sendProgressReportsWithParent: (organizationId: string, studentIds?: string[]) =>
    api.post<{ success: boolean, message: string, data: { emailed: number, skipped: number }, error?: string }>('/coaching/progress-reports/send-with-parent', { organizationId, studentIds }),

  // Coaching expansion features
  packageSessionHomework: (sessionId: string, data: { studentIds?: string[]; dueDate?: string; title?: string; prompt?: string }) =>
    api.post<{ success: boolean, data: { homeworkIds: string[] }, error?: string }>(`/coaching/sessions/${sessionId}/package-homework`, data),
  getSessionReplay: (sessionId: string) =>
    api.get<{ success: boolean, data: { replay: any }, error?: string }>(`/coaching/sessions/${sessionId}/replay`),

  getLessonTemplates: (organizationId: string) =>
    api.get<{ success: boolean, data: { templates: any[] }, error?: string }>(`/coaching/lesson-templates?organizationId=${organizationId}`),
  createLessonTemplate: (data: { organizationId: string; name: string; description?: string; steps?: any[] }) =>
    api.post('/coaching/lesson-templates', data),
  deleteLessonTemplate: (id: string) => api.delete(`/coaching/lesson-templates/${id}`),
  createSessionFromTemplate: (data: { organizationId: string; templateId: string; studentIds?: string[]; scheduledAt?: string }) =>
    api.post('/coaching/sessions/from-template', data),

  getRepertoireAssignments: (organizationId: string, studentId?: string) => {
    const q = new URLSearchParams({ organizationId });
    if (studentId) q.set('studentId', studentId);
    return api.get(`/coaching/repertoire-assignments?${q}`);
  },
  assignRepertoire: (data: { organizationId: string; studentId: string; title: string; studyPayload: any; dueDate?: string }) =>
    api.post('/coaching/repertoire-assignments', data),
  completeRepertoire: (id: string) => api.put(`/coaching/repertoire-assignments/${id}/complete`),

  getBlunderAnalytics: (organizationId: string, memberId: string) =>
    api.get(`/coaching/students/${memberId}/blunder-analytics?organizationId=${organizationId}`),
  getTournamentPrep: (organizationId: string, memberId: string) =>
    api.get(`/coaching/students/${memberId}/tournament-prep?organizationId=${organizationId}`),
  assignTournamentPrep: (memberId: string, data: { organizationId: string; dueDate?: string }) =>
    api.post(`/coaching/students/${memberId}/tournament-prep/assign`, data),

  getCurricula: (organizationId: string) =>
    api.get(`/coaching/curricula?organizationId=${organizationId}`),
  createCurriculum: (data: any) => api.post('/coaching/curricula', data),
  enrollCurriculum: (curriculumId: string) => api.post(`/coaching/curricula/${curriculumId}/enroll`),
  advanceCurriculumWeek: (curriculumId: string, data: { studentId: string }) =>
    api.post(`/coaching/curricula/${curriculumId}/advance-week`, data),

  getSpacedRepDue: (organizationId: string) =>
    api.get(`/coaching/spaced-repetition/due?organizationId=${organizationId}`),
  recordSpacedReview: (id: string, quality?: number) =>
    api.post(`/coaching/spaced-repetition/${id}/review`, { quality }),

  joinGroupClassWaitlist: (classId: string) => api.post(`/coaching/group-classes/${classId}/waitlist`),
  leaveGroupClassWaitlist: (classId: string) => api.delete(`/coaching/group-classes/${classId}/waitlist`),
  getGroupClassWaitlist: (classId: string) => api.get(`/coaching/group-classes/${classId}/waitlist`),

  getLessonPackages: (organizationId: string) =>
    api.get(`/coaching/lesson-packages?organizationId=${organizationId}`),
  createLessonPackage: (data: { organizationId: string; name: string; lessonCount: number; priceCents?: number }) =>
    api.post('/coaching/lesson-packages', data),
  purchaseLessonPackage: (packageId: string, data?: { method?: 'stripe' | 'paypal' | 'wallet' }) =>
    api.post(`/coaching/lesson-packages/${packageId}/purchase`, data || {}),
  checkoutLessonPackage: (packageId: string, data?: { method?: 'stripe' | 'paypal' | 'wallet' }) =>
    api.post(`/coaching/lesson-packages/${packageId}/checkout`, data || {}),
  confirmLessonPackage: (packageId: string, sessionId: string) =>
    api.post(`/coaching/lesson-packages/${packageId}/confirm`, { sessionId }),
  confirmLessonPackagePaypal: (packageId: string, orderId: string) =>
    api.post(`/coaching/lesson-packages/${packageId}/confirm-paypal`, { orderId }),
  checkoutBooking: (bookingId: string, data?: { method?: 'stripe' | 'paypal' | 'wallet' }) =>
    api.post(`/coaching/bookings/${bookingId}/checkout`, data || {}),
  confirmBookingPayment: (bookingId: string, sessionId: string) =>
    api.post(`/coaching/bookings/${bookingId}/confirm`, { sessionId }),
  confirmBookingPaypal: (bookingId: string, orderId: string) =>
    api.post(`/coaching/bookings/${bookingId}/confirm-paypal`, { orderId }),
  getLessonPackageBalances: (organizationId: string) =>
    api.get(`/coaching/lesson-packages/balances?organizationId=${organizationId}`),
  getPayments: (organizationId: string, limit = 100) =>
    api.get<{ success: boolean, data: { payments: any[], summary: any }, error?: string }>(
      `/coaching/payments?organizationId=${organizationId}&limit=${limit}`
    ),

  updateParentEmail: (memberId: string, data: { organizationId: string; parentEmail: string }) =>
    api.put(`/coaching/members/${memberId}/parent-email`, data),

  getCoachingStreaks: (organizationId: string, studentId?: string) => {
    const q = new URLSearchParams({ organizationId });
    if (studentId) q.set('studentId', studentId);
    return api.get(`/coaching/coaching-streaks?${q}`);
  },
  issueLevelCertificate: (data: { organizationId: string; studentId: string; levelId: string; levelName: string }) =>
    api.post('/coaching/level-certificates/issue', data),
  getMyCertificates: (organizationId: string) =>
    api.get(`/coaching/level-certificates/mine?organizationId=${organizationId}`),

  getSpotlightNotes: (organizationId: string, studentId: string) =>
    api.get(`/coaching/spotlight-notes?organizationId=${organizationId}&studentId=${studentId}`),
  createSpotlightNote: (data: { organizationId: string; studentId: string; fen?: string; moveIndex?: number; textContent?: string; audioUrl?: string }) =>
    api.post('/coaching/spotlight-notes', data),

  getBookingSuggestions: (organizationId: string, coachId: string) =>
    api.get(`/coaching/booking-suggestions?organizationId=${organizationId}&coachId=${coachId}`),
};

// Club Events API
export const clubEventsApi = {
  getEvents: (organizationId: string, params?: { upcoming?: boolean; checkInWindow?: boolean; status?: string }) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (params?.upcoming) queryParams.append('upcoming', '1');
    if (params?.checkInWindow) queryParams.append('checkInWindow', '1');
    if (params?.status) queryParams.append('status', params.status);
    return api.get<{ success: boolean, data: { events: any[] }, error?: string }>(`/club-events?${queryParams.toString()}`);
  },
  getPublicEvents: (organizationId: string) =>
    api.get<{ success: boolean, data: { events: any[] }, error?: string }>(`/club-events/public?organizationId=${organizationId}`),
  getPublicSchedule: (organizationId: string) =>
    api.get<{ success: boolean, data: { days: Array<{ dayOfWeek: number, items: any[] }> }, error?: string }>(`/club-events/public/schedule?organizationId=${organizationId}`),
  getCalendarFeedUrl: (organizationId: string) =>
    `/api/club-events/public/calendar.ics?organizationId=${organizationId}`,
  createEvent: (data: any) =>
    api.post<{ success: boolean, message: string, data: { id: string, seriesId?: string | null, occurrences?: number }, error?: string }>('/club-events', data),
  updateEvent: (id: string, data: any) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/club-events/${id}`, data),
  deleteEvent: (id: string, series?: boolean) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/club-events/${id}${series ? '?series=1' : ''}`),
  rsvp: (id: string, status: 'going' | 'maybe' | 'declined', memberId?: string) =>
    api.post<{ success: boolean, message: string, data: { status: string }, error?: string }>(`/club-events/${id}/rsvp`, { status, ...(memberId ? { memberId } : {}) }),
  getRsvps: (id: string) =>
    api.get<{ success: boolean, data: { rsvps: any[], counts: any }, error?: string }>(`/club-events/${id}/rsvps`),
  saveAttendance: (id: string, records: Array<{ memberId: string; attended: boolean }>) =>
    api.post<{ success: boolean, message: string, data: { updated: number }, error?: string }>(`/club-events/${id}/attendance`, { records }),
  checkout: (id: string, data?: { method?: 'stripe' | 'paypal' | 'wallet'; successUrl?: string; cancelUrl?: string }) =>
    api.post<{ success: boolean, data: { checkoutUrl?: string, sessionId?: string, orderId?: string, paid?: boolean, method?: string }, message?: string, error?: string }>(`/club-events/${id}/checkout`, data || {}),
  confirmPayment: (id: string, sessionId: string) =>
    api.post<{ success: boolean, message: string, data: { status: string, paymentStatus: string }, error?: string }>(`/club-events/${id}/confirm`, { sessionId }),
  confirmPaypal: (id: string, orderId: string) =>
    api.post<{ success: boolean, message: string, data: { status: string, paymentStatus: string }, error?: string }>(`/club-events/${id}/confirm-paypal`, { orderId }),
};

// Club Arena API (live matchmaking — find an opponent and play on Lichess)
export interface ArenaTimeControl {
  clockLimit: number;
  clockIncrement: number;
  label?: string;
  category?: string;
}

export type ArenaPlatform = 'local' | 'lichess' | 'chesscom';

/** A variant the arena offers, and the sites it is played on. Keys are Lichess's. */
export interface ArenaVariant {
  key: string;
  label: string;
  platforms: ArenaPlatform[];
  description: string;
}

export interface ArenaSeek {
  id: string;
  userId: string;
  displayName: string;
  lichessUsername: string | null;
  chesscomUsername: string | null;
  rating: number | null;
  timeControls: ArenaTimeControl[];
  timeControlLabels: string[];
  platforms: ArenaPlatform[];
  /** Takes whatever time control the other side wants. */
  anyTimeControl?: boolean;
  ratedPreference: 'rated' | 'unrated' | 'any';
  rated: boolean | null;
  variant: string;
  variantLabel?: string;
  maxRatingDiff: number;
  /**
   * Set on other members' seeks while you are seeking yourself: why the two
   * of you are not being paired, or null when nothing is in the way.
   */
  pairing?: { code: 'variant' | 'platform' | 'time' | 'rated' | 'rating'; text: string; temporary: boolean } | null;
  status: 'waiting' | 'matched' | 'cancelled' | 'expired' | 'failed';
  error: string | null;
  createdAt: string | null;
  expiresAt: string | null;
}

export interface ArenaGame {
  id: string;
  whiteUserId: string;
  blackUserId: string;
  whiteName: string;
  blackName: string;
  whiteUsername: string | null;
  blackUsername: string | null;
  platform: ArenaPlatform;
  platformLabel: string;
  lichessGameId: string | null;
  gameUrl: string | null;
  /** On Chess.com each player opens their own challenge link. */
  whiteLink: string | null;
  blackLink: string | null;
  timeControl: string;
  rated: boolean;
  variant: string;
  variantLabel?: string;
  status: 'ongoing' | 'finished' | 'aborted' | 'failed';
  result: string | null;
  error: string | null;
  createdAt: string | null;
  finishedAt: string | null;
}

export interface ArenaLobby {
  waiting: ArenaSeek[];
  waitingCount: number;
  liveGames: ArenaGame[];
  recentGames: ArenaGame[];
  mySeek: ArenaSeek | null;
  myGame: ArenaGame | null;
  myColor: 'white' | 'black' | null;
  /** Your last matchup, when it was made but the game never started. */
  lastFailure?: { gameId: string; opponent: string; platformLabel: string; error: string | null; at: string | null } | null;
  /** The link this member should open for their own game. */
  myLink: string | null;
  /** Per site: the account we know, and whether a game can be opened with it. */
  linked: Partial<Record<ArenaPlatform, { username: string | null; ready: boolean; needsAuthorization: boolean } | null>>;
  /** Sites playable right now. */
  availablePlatforms: ArenaPlatform[];
  /** Sites where the username is known but authorization is still missing. */
  pendingPlatforms: ArenaPlatform[];
  lichessLinked: boolean;
  lichessUsername: string | null;
  chesscomLinked: boolean;
  chesscomUsername: string | null;
  timeControls: ArenaTimeControl[];
  platforms: ArenaPlatform[];
  /** Absent from a server older than the variants. */
  variants?: ArenaVariant[];
}

export interface ArenaSeekRequest {
  organizationId: string;
  timeControls: ArenaTimeControl[];
  /** Play whatever time control the other side wants. */
  anyTimeControl?: boolean;
  platforms?: ArenaPlatform[];
  ratedPreference?: 'rated' | 'unrated' | 'any';
  variant?: string;
  maxRatingDiff?: number;
  ttlMinutes?: number;
}

export const clubArenaApi = {
  getTimeControls: () =>
    api.get<{ success: boolean; data: { timeControls: ArenaTimeControl[]; platforms: ArenaPlatform[] } }>(
      '/club-arena/time-controls'
    ),
  getLobby: (organizationId: string) =>
    api.get<{ success: boolean; data: ArenaLobby; error?: string }>(
      `/club-arena/lobby?organizationId=${encodeURIComponent(organizationId)}`
    ),
  seek: (data: ArenaSeekRequest) =>
    api.post<{ success: boolean; message: string; data: ArenaLobby & { seek: ArenaSeek; game: ArenaGame | null }; error?: string; code?: string }>(
      '/club-arena/seek',
      data
    ),
  cancelSeek: (organizationId: string) =>
    api.delete<{ success: boolean; message: string; data: { cancelled: boolean }; error?: string }>(
      `/club-arena/seek?organizationId=${encodeURIComponent(organizationId)}`
    ),
  getGames: (organizationId: string, params?: { mine?: boolean; limit?: number }) => {
    const query = new URLSearchParams({ organizationId });
    if (params?.mine) query.append('mine', '1');
    if (params?.limit) query.append('limit', String(params.limit));
    return api.get<{ success: boolean; data: { games: ArenaGame[] }; error?: string }>(
      `/club-arena/games?${query.toString()}`
    );
  },
  refresh: (organizationId: string) =>
    api.post<{ success: boolean; data: ArenaLobby; error?: string }>('/club-arena/refresh', { organizationId }),
};

// Club Billing API (membership tiers + dues)
export const clubBillingApi = {
  getTiers: (organizationId: string) =>
    api.get<{ success: boolean, data: { tiers: any[] }, error?: string }>(`/club-billing/tiers?organizationId=${organizationId}`),
  createTier: (data: any) =>
    api.post<{ success: boolean, message: string, data: { id: string }, error?: string }>('/club-billing/tiers', data),
  updateTier: (id: string, data: any) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/club-billing/tiers/${id}`, data),
  deleteTier: (id: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/club-billing/tiers/${id}`),
  assignMemberTier: (memberId: string, organizationId: string, tierId: string | null) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/club-billing/members/${memberId}/tier`, { organizationId, tierId }),
  getDues: (organizationId: string, params?: { status?: string; memberId?: string }) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (params?.status) queryParams.append('status', params.status);
    if (params?.memberId) queryParams.append('memberId', params.memberId);
    return api.get<{ success: boolean, data: { dues: any[] }, error?: string }>(`/club-billing/dues?${queryParams.toString()}`);
  },
  getMyDues: (organizationId: string) =>
    api.get<{ success: boolean, data: { dues: any[] }, error?: string }>(`/club-billing/dues/mine?organizationId=${organizationId}`),
  createDues: (data: { organizationId: string; memberIds: string[]; tierId?: string; amountCents: number; currency?: string; dueDate?: string; periodStart?: string; periodEnd?: string; description?: string }) =>
    api.post<{ success: boolean, message: string, data: { created: number, ids: string[] }, error?: string }>('/club-billing/dues', data),
  generateDues: (data: { organizationId: string; tierId: string; dueDate?: string; periodStart?: string; periodEnd?: string }) =>
    api.post<{ success: boolean, message: string, data: { created: number, skipped: number }, error?: string }>('/club-billing/dues/generate', data),
  updateDues: (id: string, data: { action?: 'markPaid' | 'waive' | 'markPending'; paymentMethod?: string; paymentReference?: string; notes?: string }) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/club-billing/dues/${id}`, data),
  deleteDues: (id: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/club-billing/dues/${id}`),
  getSummary: (organizationId: string) =>
    api.get<{ success: boolean, data: { summary: any }, error?: string }>(`/club-billing/summary?organizationId=${organizationId}`),
  getTransactions: (organizationId: string, params?: { sourceType?: string; limit?: number }) => {
    const q = new URLSearchParams({ organizationId });
    if (params?.sourceType) q.set('sourceType', params.sourceType);
    if (params?.limit) q.set('limit', String(params.limit));
    return api.get<{ success: boolean, data: { transactions: any[], revenue: any }, error?: string }>(
      `/club-billing/transactions?${q.toString()}`
    );
  },
  sendDuesReminders: (organizationId: string, duesIds: string[]) =>
    api.post<{ success: boolean, message: string, data: { sent: number, skipped: number }, error?: string }>('/club-billing/dues/remind', { organizationId, duesIds }),
  createDuesCheckout: (duesId: string, data?: { method?: 'stripe' | 'paypal' | 'wallet' }) =>
    api.post<{ success: boolean, data: { checkoutUrl?: string, sessionId?: string, orderId?: string, paid?: boolean, method?: string }, message?: string, error?: string }>(`/club-billing/dues/${duesId}/checkout`, data || {}),
  confirmDuesCheckout: (duesId: string, sessionId: string) =>
    api.post<{ success: boolean, message: string, data: { status: string }, error?: string }>(`/club-billing/dues/${duesId}/confirm`, { sessionId }),
  confirmDuesPaypal: (duesId: string, orderId: string) =>
    api.post<{ success: boolean, message: string, data: { status: string }, error?: string }>(`/club-billing/dues/${duesId}/confirm-paypal`, { orderId }),
  subscribeTierCheckout: (tierId: string) =>
    api.post<{ success: boolean, message?: string, data: { checkoutUrl?: string, sessionId?: string, free?: boolean }, error?: string }>(`/club-billing/tiers/${tierId}/subscribe-checkout`, {}),
  confirmTierSubscribe: (tierId: string, sessionId: string) =>
    api.post<{ success: boolean, message: string, data: { status: string, subscriptionId?: string | null }, error?: string }>(`/club-billing/tiers/${tierId}/subscribe-confirm`, { sessionId }),
  getWaivers: (organizationId: string) =>
    api.get<{ success: boolean, data: { waivers: any[] }, error?: string }>(`/club-billing/waivers?organizationId=${organizationId}`),
  createWaiver: (data: { organizationId: string; code: string; description?: string; discountPercent?: number; maxUses?: number; expiresAt?: string; appliesTo?: string }) =>
    api.post<{ success: boolean, message: string, data: { id: string }, error?: string }>('/club-billing/waivers', data),
  deleteWaiver: (id: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/club-billing/waivers/${id}`),
  redeemWaiver: (data: { organizationId: string; code: string; appliesTo: string; targetId: string }) =>
    api.post<{ success: boolean, message: string, error?: string }>('/club-billing/waivers/redeem', data),
  createPortalSession: (organizationId: string, returnUrl?: string) =>
    api.post<{ success: boolean, data: { url: string }, error?: string }>('/club-billing/portal-session', { organizationId, returnUrl }),
};

// Club training level tracks (custom milestones)
export const clubTrainingLevelsApi = {
  getLevels: (organizationId: string) =>
    api.get<{ success: boolean, data: { levels: any[] }, error?: string }>(`/club-training-levels?organizationId=${organizationId}`),
  saveLevels: (organizationId: string, levels: Array<{ id?: string; name: string; description?: string; minRating?: number; color?: string; sortOrder?: number }>) =>
    api.put<{ success: boolean, message: string, data: { levels: any[] }, error?: string }>('/club-training-levels', { organizationId, levels }),
};

// Club Programs API (class series + camps with paid enrollment)
export const clubProgramsApi = {
  getPrograms: (organizationId: string) =>
    api.get<{ success: boolean, data: { programs: any[] }, error?: string }>(`/club-programs?organizationId=${organizationId}`),
  getPublicPrograms: (organizationId: string) =>
    api.get<{ success: boolean, data: { programs: any[] }, error?: string }>(`/club-programs/public?organizationId=${organizationId}`),
  createProgram: (data: any) =>
    api.post<{ success: boolean, message: string, data: { id: string }, error?: string }>('/club-programs', data),
  updateProgram: (id: string, data: any) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/club-programs/${id}`, data),
  deleteProgram: (id: string) =>
    api.delete<{ success: boolean, message: string, error?: string }>(`/club-programs/${id}`),
  enroll: (id: string) =>
    api.post<{ success: boolean, message: string, data: { status: string, paymentStatus: string }, error?: string }>(`/club-programs/${id}/enroll`, {}),
  createCheckout: (id: string, data?: { method?: 'stripe' | 'paypal' | 'wallet' }) =>
    api.post<{ success: boolean, data: { checkoutUrl?: string, sessionId?: string, orderId?: string, paid?: boolean, method?: string }, message?: string, error?: string }>(`/club-programs/${id}/checkout`, data || {}),
  confirmCheckout: (id: string, sessionId: string) =>
    api.post<{ success: boolean, message: string, data: { status: string }, error?: string }>(`/club-programs/${id}/confirm`, { sessionId }),
  confirmPaypal: (id: string, orderId: string) =>
    api.post<{ success: boolean, message: string, data: { status: string }, error?: string }>(`/club-programs/${id}/confirm-paypal`, { orderId }),
  getRoster: (id: string) =>
    api.get<{ success: boolean, data: { roster: any[] }, error?: string }>(`/club-programs/${id}/roster`),
  updateEnrollment: (id: string, enrollmentId: string, data: { status?: string; paymentStatus?: string }) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/club-programs/${id}/roster/${enrollmentId}`, data),
  generateSessions: (id: string) =>
    api.post<{ success: boolean, message: string, data: { created: number }, error?: string }>(`/club-programs/${id}/generate-sessions`, {}),
};

// Club payment methods (Stripe / PayPal / wallet availability)
export const clubPaymentsApi = {
  getMethods: (organizationId: string) =>
    api.get<{ success: boolean, data: { stripe: boolean, paypal: boolean, wallet: boolean, walletBalance?: { totalSpendableCents: number } }, error?: string }>(
      `/club-payments/methods?organizationId=${organizationId}`
    ),
};

// Club Donations API (donations + sponsor-a-student)
export const clubDonationsApi = {
  createCheckout: (data: { organizationId?: string; organizationSlug?: string; amountCents: number; donationType?: 'general' | 'sponsor_student'; donorName?: string; donorEmail?: string; message?: string; successUrl?: string; cancelUrl?: string }) =>
    api.post<{ success: boolean, data: { checkoutUrl: string, sessionId: string, donationId: string }, error?: string }>('/club-donations/checkout', data),
  confirm: (donationId: string, sessionId: string) =>
    api.post<{ success: boolean, message: string, data: { status: string }, error?: string }>(`/club-donations/${donationId}/confirm`, { sessionId }),
  list: (organizationId: string) =>
    api.get<{ success: boolean, data: { donations: any[], totals: { count: number, totalCents: number } }, error?: string }>(`/club-donations?organizationId=${organizationId}`),
};

// Club Inquiries API (lead capture)
export const clubInquiriesApi = {
  submit: (data: { organizationId?: string; organizationSlug?: string; name: string; email: string; phone?: string; interest?: string; message?: string }) =>
    api.post<{ success: boolean, message: string, error?: string }>('/club-inquiries/public', data),
  list: (organizationId: string, status?: string) =>
    api.get<{ success: boolean, data: { inquiries: any[] }, error?: string }>(`/club-inquiries?organizationId=${organizationId}${status ? `&status=${status}` : ''}`),
  update: (id: string, data: { status?: string; adminNotes?: string }) =>
    api.put<{ success: boolean, message: string, error?: string }>(`/club-inquiries/${id}`, data),
};

// Club Management API (lifecycle, operations, community, competition, finance extras, growth, compliance, integrations)
export const clubManagementApi = {
  // Lifecycle
  getLifecycleConfig: (organizationId: string) =>
    api.get(`/club-management/lifecycle/config?organizationId=${organizationId}`),
  updateLifecycleConfig: (data: { organizationId: string; gracePeriodDays?: number; autoSuspend?: boolean; renewalReminderDays?: number; onboardingEnabled?: boolean }) =>
    api.put('/club-management/lifecycle/config', data),
  processLapsedMembers: (organizationId: string) =>
    api.post('/club-management/lifecycle/process-lapsed', { organizationId }),
  getOnboardingSteps: (organizationId: string) =>
    api.get(`/club-management/onboarding/steps?organizationId=${organizationId}`),
  createOnboardingStep: (data: { organizationId: string; title: string; description?: string; stepType?: string; sortOrder?: number; isRequired?: boolean }) =>
    api.post('/club-management/onboarding/steps', data),
  deleteOnboardingStep: (id: string) => api.delete(`/club-management/onboarding/steps/${id}`),
  getMyOnboarding: (organizationId: string) =>
    api.get(`/club-management/onboarding/mine?organizationId=${organizationId}`),
  completeOnboardingStep: (organizationId: string, stepId: string) =>
    api.post('/club-management/onboarding/complete', { organizationId, stepId }),
  getHouseholds: (organizationId: string) =>
    api.get(`/club-management/households?organizationId=${organizationId}`),
  createHousehold: (data: { organizationId: string; name: string; billingMemberId?: string; memberIds?: string[] }) =>
    api.post('/club-management/households', data),
  getScholarships: (organizationId: string) =>
    api.get(`/club-management/scholarships?organizationId=${organizationId}`),
  submitScholarship: (data: { organizationId: string; applicantName: string; applicantEmail?: string; reason?: string; householdIncome?: string }) =>
    api.post('/club-management/scholarships', data),
  updateScholarship: (id: string, data: { status?: string; discountPercent?: number; adminNotes?: string }) =>
    api.put(`/club-management/scholarships/${id}`, data),
  joinEventWaitlist: (eventId: string) => api.post(`/club-management/events/${eventId}/waitlist`),
  getEventWaitlist: (eventId: string) => api.get(`/club-management/events/${eventId}/waitlist`),
  selfCheckIn: (eventId: string, checkInCode?: string) =>
    api.post(`/club-management/events/${eventId}/check-in`, { checkInCode }),
  // Operations
  getVolunteerShifts: (organizationId: string) =>
    api.get(`/club-management/volunteers/shifts?organizationId=${organizationId}`),
  createVolunteerShift: (data: any) => api.post('/club-management/volunteers/shifts', data),
  signupVolunteerShift: (shiftId: string) => api.post(`/club-management/volunteers/shifts/${shiftId}/signup`),
  getEquipment: (organizationId: string) =>
    api.get(`/club-management/equipment?organizationId=${organizationId}`),
  createEquipment: (data: any) => api.post('/club-management/equipment', data),
  loanEquipment: (id: string, data: { memberId: string; dueBackAt?: string; notes?: string }) =>
    api.post(`/club-management/equipment/${id}/loan`, data),
  returnEquipment: (id: string) => api.post(`/club-management/equipment/${id}/return`),
  getIncidents: (organizationId: string, studentId?: string) =>
    api.get(`/club-management/incidents?organizationId=${organizationId}${studentId ? `&studentId=${studentId}` : ''}`),
  createIncident: (data: any) => api.post('/club-management/incidents', data),
  // Community
  getDirectory: (organizationId: string) =>
    api.get(`/club-management/directory?organizationId=${organizationId}`),
  updateDirectoryProfile: (data: any) => api.put('/club-management/directory/profile', data),
  getLadder: (organizationId: string) =>
    api.get(`/club-management/ladder?organizationId=${organizationId}`),
  recordLadderMatch: (data: any) => api.post('/club-management/ladder/matches', data),
  getStudyGroups: (organizationId: string) =>
    api.get(`/club-management/study-groups?organizationId=${organizationId}`),
  createStudyGroup: (data: any) => api.post('/club-management/study-groups', data),
  joinStudyGroup: (groupId: string) => api.post(`/club-management/study-groups/${groupId}/join`),
  getStudyGroupPosts: (groupId: string) => api.get(`/club-management/study-groups/${groupId}/posts`),
  createStudyGroupPost: (groupId: string, data: { title?: string; body: string }) =>
    api.post(`/club-management/study-groups/${groupId}/posts`, data),
  getBadges: (organizationId: string) =>
    api.get(`/club-management/badges?organizationId=${organizationId}`),
  createBadge: (data: any) => api.post('/club-management/badges', data),
  awardBadge: (badgeId: string, memberId: string) =>
    api.post(`/club-management/badges/${badgeId}/award`, { memberId }),
  getMyBadges: (organizationId: string) =>
    api.get(`/club-management/badges/mine?organizationId=${organizationId}`),
  // Competition
  getChampionships: (organizationId: string) =>
    api.get(`/club-management/championships?organizationId=${organizationId}`),
  createChampionship: (data: any) => api.post('/club-management/championships', data),
  addChampionshipRound: (championshipId: string, data: any) =>
    api.post(`/club-management/championships/${championshipId}/rounds`, data),
  getTeamMatches: (organizationId: string) =>
    api.get(`/club-management/team-matches?organizationId=${organizationId}`),
  createTeamMatch: (data: any) => api.post('/club-management/team-matches', data),
  // Coaching extras
  getResources: (organizationId: string) =>
    api.get(`/club-management/resources?organizationId=${organizationId}`),
  createResource: (data: any) => api.post('/club-management/resources', data),
  getMakeupCredits: (organizationId: string, memberId?: string) =>
    api.get(`/club-management/makeup-credits?organizationId=${organizationId}${memberId ? `&memberId=${memberId}` : ''}`),
  grantMakeupCredit: (data: any) => api.post('/club-management/makeup-credits', data),
  saveProgramAttendance: (programId: string, records: Array<{ memberId: string; sessionDate: string; attended: boolean }>) =>
    api.post(`/club-management/programs/${programId}/attendance`, { records }),
  getGoals: (organizationId: string, studentId?: string) =>
    api.get(`/club-management/goals?organizationId=${organizationId}${studentId ? `&studentId=${studentId}` : ''}`),
  createGoal: (data: any) => api.post('/club-management/goals', data),
  updateGoal: (id: string, data: { status: string }) => api.put(`/club-management/goals/${id}`, data),
  // Finance extras
  getExpenses: (organizationId: string) =>
    api.get(`/club-management/expenses?organizationId=${organizationId}`),
  createExpense: (data: any) => api.post('/club-management/expenses', data),
  getPayouts: (organizationId: string) =>
    api.get(`/club-management/payouts?organizationId=${organizationId}`),
  createPayout: (data: any) => api.post('/club-management/payouts', data),
  markPayoutPaid: (id: string) => api.put(`/club-management/payouts/${id}`),
  getAgingReport: (organizationId: string) =>
    api.get(`/club-management/aging-report?organizationId=${organizationId}`),
  issueTaxReceipt: (organizationId: string, memberId: string, taxYear: number) =>
    api.post('/club-management/tax-receipts', { organizationId, memberId, taxYear }),
  getMyTaxReceipts: (organizationId: string) =>
    api.get(`/club-management/tax-receipts/mine?organizationId=${organizationId}`),
  // Growth
  getReferrals: (organizationId: string) =>
    api.get(`/club-management/referrals?organizationId=${organizationId}`),
  createReferral: (data: any) => api.post('/club-management/referrals', data),
  redeemReferral: (organizationId: string, code: string) =>
    api.post('/club-management/referrals/redeem', { organizationId, code }),
  getTrialSlots: (organizationId: string) =>
    api.get(`/club-management/trial-slots?organizationId=${organizationId}`),
  createTrialSlot: (data: any) => api.post('/club-management/trial-slots', data),
  bookTrialSlot: (slotId: string, data: { name: string; email: string; phone?: string }) =>
    api.post(`/club-management/trial-slots/${slotId}/book`, data),
  updateInquiryPipeline: (id: string, data: { pipelineStage?: string; adminNotes?: string }) =>
    api.put(`/club-management/inquiries/${id}/pipeline`, data),
  sendSms: (data: { organizationId: string; recipient: string; message: string; messageType?: string }) =>
    api.post('/club-management/sms/send', data),
  // Compliance
  getWaivers: (organizationId: string) =>
    api.get(`/club-management/waivers?organizationId=${organizationId}`),
  createWaiver: (data: any) => api.post('/club-management/waivers', data),
  signWaiver: (waiverId: string, signedName: string) =>
    api.post(`/club-management/waivers/${waiverId}/sign`, { signedName }),
  getCoachCompliance: (organizationId: string) =>
    api.get(`/club-management/compliance/coaches?organizationId=${organizationId}`),
  saveCoachCompliance: (data: any) => api.post('/club-management/compliance/coaches', data),
  getMyConsents: (organizationId: string) =>
    api.get(`/club-management/consents/mine?organizationId=${organizationId}`),
  updateConsent: (data: { organizationId: string; consentType: string; granted: boolean }) =>
    api.put('/club-management/consents', data),
  createMinorApproval: (data: { organizationId: string; memberId: string; guardianEmail: string }) =>
    api.post('/club-management/minor-approvals', data),
  // Integrations
  getIntegrations: (organizationId: string) =>
    api.get(`/club-management/integrations?organizationId=${organizationId}`),
  saveIntegration: (type: string, data: { organizationId: string; config: any; isEnabled: boolean }) =>
    api.put(`/club-management/integrations/${type}`, data),
  syncGoogleCalendar: (organizationId: string) =>
    api.post('/club-management/integrations/google-calendar/sync', { organizationId }),
};

// Player API
export const playerApi = {
  getByTournament: (tournamentId: string) =>
    api.get<{ success: boolean, data: Player[], error?: string }>(`/players/tournament/${tournamentId}`),
  getById: (id: string) => api.get<{ success: boolean, data: Player, error?: string }>(`/players/${id}`),
  create: (player: Omit<Player, 'id' | 'created_at'>) =>
    api.post<{ success: boolean, data: Player, error?: string }>('/players', player),
  update: (id: string, player: Partial<Player>) =>
    api.put(`/players/${id}`, player),
  delete: (id: string) => api.delete(`/players/${id}`),
  bulkCreate: (tournamentId: string, players: Omit<Player, 'id' | 'tournament_id' | 'created_at'>[]) =>
    api.post('/players/bulk', { tournament_id: tournamentId, players }),
  search: (searchTerm: string, limit?: number, tournamentId?: string, searchType?: 'both' | 'uscf' | 'fide') =>
    api.get<{ success: boolean, data: { players: any[], count: number }, error?: string }>(`/players/search?q=${encodeURIComponent(searchTerm)}${limit ? `&limit=${limit}` : ''}${tournamentId ? `&tournamentId=${encodeURIComponent(tournamentId)}` : ''}${searchType ? `&searchType=${searchType}` : ''}`),
  getDetails: (uscfId: string) =>
    api.get<any>(`/players/details/${uscfId}`),
  downloadTemplate: () =>
    api.get('/players/csv-template', { responseType: 'blob' }),
  downloadExcelTemplate: () =>
    api.get('/players/excel-template', { responseType: 'blob' }),
  uploadCSV: (file: File, tournamentId: string, lookupRatings: boolean = true) => {
    const formData = new FormData();
    formData.append('csvFile', file);
    formData.append('tournament_id', tournamentId);
    formData.append('lookup_ratings', lookupRatings.toString());
    return api.post('/players/csv-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadExcel: (file: File, tournamentId: string, lookupRatings: boolean = true) => {
    const formData = new FormData();
    formData.append('excelFile', file);
    formData.append('tournament_id', tournamentId);
    formData.append('lookup_ratings', lookupRatings.toString());
    return api.post('/players/excel-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getTournamentReentries: (tournamentId: string) =>
    api.get<{ success: boolean; data: ReEntryRecord[]; error?: string }>(
      `/players/reentries/tournament/${tournamentId}`
    ),
  reenter: (
    playerId: string,
    payload: {
      to_section: string;
      round?: number | string | null;
      notes?: string;
    }
  ) =>
    api.post<{
      success: boolean;
      message: string;
      data?: { player: Player; reentry?: ReEntryRecord };
      error?: string;
    }>(`/players/${playerId}/reentry`, payload),
  clearReentry: (playerId: string) =>
    api.delete<{ success: boolean; message: string; data?: any; error?: string }>(
      `/players/${playerId}/reentry`
    ),
  importCSV: (tournamentId: string, players: any[], lookupRatings: boolean = true) =>
    api.post('/players/csv-import', { tournament_id: tournamentId, players, lookup_ratings: lookupRatings }),
  importExcel: (tournamentId: string, players: any[], lookupRatings: boolean = true) =>
    api.post('/players/excel-import', { tournament_id: tournamentId, players, lookup_ratings: lookupRatings }),
  setInactiveRound: (playerId: string, round: number, reason?: string) =>
    api.post(`/players/${playerId}/inactive-round`, { round, reason }),
  removeInactiveRound: (playerId: string, round: number) =>
    api.delete(`/players/${playerId}/inactive-round/${round}`),
  getInactiveRounds: (playerId: string) =>
    api.get<{ success: boolean, data: PlayerInactiveRound[], error?: string }>(`/players/${playerId}/inactive-rounds`),
  getTournamentInactiveRounds: (tournamentId: string) =>
    api.get<{ success: boolean, data: PlayerInactiveRound[], error?: string }>(`/players/tournament/${tournamentId}/inactive-rounds`),
  deleteDuplicates: (tournamentId: string, criteria: 'name' | 'uscf_id' | 'both' = 'name') =>
    api.post<{
      success: boolean;
      message: string;
      deletedCount: number;
      duplicates: Array<{
        key: string;
        kept: { id: string; name: string; uscf_id?: string };
        deleted: Array<{ id: string; name: string; uscf_id?: string }>;
      }>;
      deletedPlayers: Array<{
        id: string;
        name: string;
        uscf_id?: string;
        reason: string;
      }>;
    }>(`/players/delete-duplicates/${tournamentId}`, { criteria }),
  processAITextImport: (tournamentId: string, text: string) =>
    api.post<{
      success: boolean;
      data: {
        players?: any[];
        count?: number;
        extracted_count?: number;
        imported_count?: number;
        player_ids?: string[];
        rating_lookup_results?: any[];
        import_errors?: any[];
        duplicates_removed?: number;
      };
      error?: string;
    }>('/players/ai-text-import', { tournament_id: tournamentId, text }, {
      timeout: 120000 // 120 second timeout for AI processing
    }),
};

// Upload API
export const uploadApi = {
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ success: boolean; data: { url: string; filename: string }; error?: string }>('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Pairing API with enhanced round independence
export const pairingApi = {
  getByRound: (tournamentId: string, round: number, sectionName?: string) =>
    api.get<Pairing[]>(`/pairings/tournament/${tournamentId}/round/${round}${sectionName ? `?section=${sectionName}` : ''}`),

  getBySection: (tournamentId: string, sectionName: string) =>
    api.get<Pairing[]>(`/pairings/tournament/${tournamentId}/section/${sectionName}`),

  // Get all pairings for a tournament (alias for getAllByTournament for backward compatibility)
  getAll: (tournamentId: string) =>
    api.get<Pairing[]>(`/pairings/tournament/${tournamentId}`),

  // NEW: Get all pairings grouped by round for independent round management
  getAllByTournament: (tournamentId: string) =>
    api.get<{
      success: boolean;
      tournamentId: string;
      pairingsByRound: Record<string, Pairing[]>;
      totalRounds: number;
      rounds: Array<{
        round: number;
        pairingsCount: number;
        sections: string[];
      }>;
    }>(`/pairings/tournament/${tournamentId}/all`),

  // NEW: Get status for all rounds
  getAllRoundsStatus: (tournamentId: string) =>
    api.get<{
      success: boolean;
      tournamentId: string;
      totalRounds: number;
      roundsStatus: Record<string, any>;
    }>(`/pairings/tournament/${tournamentId}/rounds/status`),

  getDisplay: (tournamentId: string, options?: {
    round?: number;
    display_format?: 'default' | 'compact' | 'detailed';
    show_ratings?: boolean;
    show_uscf_ids?: boolean;
    board_numbers?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (options?.round) params.append('round', options.round.toString());
    if (options?.display_format) params.append('display_format', options.display_format);
    if (options?.show_ratings !== undefined) params.append('show_ratings', options.show_ratings.toString());
    if (options?.show_uscf_ids !== undefined) params.append('show_uscf_ids', options.show_uscf_ids.toString());
    if (options?.board_numbers !== undefined) params.append('board_numbers', options.board_numbers.toString());

    return api.get<any>(`/pairings/tournament/${tournamentId}/display?${params.toString()}`);
  },

  // Enhanced generate with round independence
  generate: (tournamentId: string, round: number, clearExisting: boolean = false) =>
    api.post<{
      success: boolean;
      message: string;
      pairings: Pairing[];
      sectionResults: any;
      metadata: any;
      roundStatus: any;
    }>('/pairings/generate', { tournamentId, round, clearExisting }),

  generateForSection: (tournamentId: string, round: number, sectionName: string, startingBoardNumber: number | string = 1) =>
    api.post('/pairings/generate/section', { tournamentId, round, sectionName, startingBoardNumber }),

  generateQuad: (tournamentId: string, quadOverflow?: any) =>
    api.post<{
      success: boolean;
      message?: string;
      error?: string;
      data?: {
        totalRounds: number;
        roundsData: Array<{
          round: number;
          quadCount: number;
          totalGames: number;
          totalByes: number;
          pairingsCount: number;
        }>;
        totalGamesAllRounds: number;
        totalByesAllRounds: number;
        message: string;
      };
    }>('/pairings/generate/quad', { tournamentId, quadOverflow }),

  generateRoundRobinPairings: (tournamentId: string, section?: string, gamesPerOpponent?: number, colorAllocationMethod?: string) =>
    api.post<{
      success: boolean;
      message: string;
      data: {
        tournamentId: string;
        section: string;
        players: number;
        rounds: number;
        gamesPerOpponent?: number;
        colorAllocationMethod?: string;
        totalGames: number;
        totalByes: number;
        pairings: any[];
      };
    }>('/pairings/generate/round-robin', {
      tournamentId,
      section,
      gamesPerOpponent,
      colorAllocationMethod
    }),

  generateSingleEliminationBracket: (tournamentId: string, section?: string) =>
    api.post<{
      success: boolean;
      message: string;
      data: {
        tournamentId: string;
        section: string;
        players: number;
        rounds: number;
        totalFirstRoundGames: number;
        totalFirstRoundByes: number;
        bracketSize: number;
        bracket: any;
      };
    }>('/pairings/generate/single-elimination', { tournamentId, section }),

  // Get bracket data for elimination tournaments
  getBracket: (tournamentId: string, section?: string) =>
    api.get<{
      success: boolean;
      data: {
        matches: Array<{
          id: string;
          round: number;
          board: number;
          white_player_id?: number;
          black_player_id?: number;
          result?: string;
          is_bye: boolean;
          bracket_position?: string;
          section?: string;
        }>;
        players: Array<{
          id: number;
          name: string;
          rating?: number;
          seed?: number;
        }>;
        totalRounds: number;
        bracketSize: number;
        structure: any;
      };
    }>(`/pairings/tournaments/${tournamentId}/bracket${section ? `?section=${section}` : ''}`,),

  // Get bracket data for team elimination tournaments
  getTeamBracket: (tournamentId: string) =>
    api.get<{
      success: boolean;
      data: {
        matches: any[];
        teams: any[];
        players: any[];
        totalRounds: number;
        bracketSize: number;
        boardsPerMatch: number;
        structure: any;
      };
    }>(`/pairings/tournaments/${tournamentId}/team-bracket`),

  // Advance elimination tournament to next round
  advanceEliminationRound: (tournamentId: string, completedRound: number, section?: string) =>
    api.post<{
      success: boolean;
      message: string;
      data: any;
      tiebreakerCreated?: boolean;
    }>('/pairings/advance-elimination', { tournamentId, completedRound, section }),

  /** Start White's clock on one club-board game. A game already going is left alone. */
  startLocalClock: (roomCode: string) =>
    api.post<{
      success: boolean;
      /** `waiting`: a seat is empty, and the clock starts when both players are at the board. */
      status: 'started' | 'waiting' | 'running' | 'in-progress' | 'over';
      message: string;
    }>('/games/start-clock', { roomCode }),

  /** Start every club-board clock in a round, optionally for one section. */
  startLocalClocksForRound: (tournamentId: string, round: number, section?: string) =>
    api.post<{
      success: boolean;
      message: string;
      started: number;
      /** Boards whose clock starts when both players are at the board. */
      waiting?: number;
      /** Each board's outcome, by room code. */
      statuses?: Record<string, 'started' | 'waiting' | 'running' | 'in-progress' | 'over' | 'missing'>;
      alreadyRunning: number;
      finished: number;
      total: number;
    }>('/games/start-all-clocks', { tournamentId, round, section }),

  generateOnlineGamesForRound: (tournamentId: string, round: number, section?: string, matchupId?: string) =>
    api.post<{
      success: boolean;
      message: string;
      created: number;
      failed: number;
      results: any[];
    }>('/pairings/generate-online-games', { tournamentId, round, section, matchupId }),

  generateTeamSwiss: (tournamentId: string, round: number, sectionName?: string) =>
    api.post<{
      success: boolean;
      message?: string;
      error?: string;
      pairings?: any[];
    }>('/pairings/generate/team-swiss', { tournamentId, round, sectionName }),

  generateBughouse: (tournamentId: string, round: number, sectionName?: string) =>
    api.post<{
      success: boolean;
      message?: string;
      error?: string;
      pairings?: any[];
    }>('/pairings/generate/bughouse', { tournamentId, round, sectionName }),

  // Section-specific pairing methods
  generateSectionPairings: (tournamentId: string, round: number, sectionName: string) =>
    api.post<{
      success: boolean;
      message: string;
      pairings: any[];
      sectionResults: any;
      sectionStatus: any;
    }>('/pairings/generate/section', { tournamentId, round, sectionName }),

  getSectionPairings: (tournamentId: string, round: number, sectionName: string) =>
    api.get<{
      success: boolean;
      tournamentId: string;
      round: number;
      section: string;
      pairings: any[];
      sectionStatus: any;
      pairingsCount: number;
    }>(`/pairings/tournament/${tournamentId}/round/${round}/section/${sectionName}`),

  getSectionStatus: (tournamentId: string, round: number, sectionName: string) =>
    api.get<{
      success: boolean;
      tournamentId: string;
      round: number;
      section: string;
      totalPairings: number;
      completedPairings: number;
      pendingPairings: number;
      percentage: number;
      isComplete: boolean;
      hasPairings: boolean;
      canGenerateNextRound: boolean;
    }>(`/pairings/tournament/${tournamentId}/round/${round}/section/${sectionName}/status`),

  generateSectionPrizes: (tournamentId: string, sectionName: string, prizes?: any[]) =>
    api.post<{
      success: boolean;
      tournamentId: string;
      section: string;
      prizeDistribution: any[];
      prizesAwarded?: any[];
      prizeMetadata?: any;
      message?: string;
      error?: string;
    }>(`/pairings/tournament/${tournamentId}/section/${encodeURIComponent(sectionName)}/prizes/generate`, {
      prizes
    }),

  generateTeamPrizes: (tournamentId: string, prizes: any[], section?: string) =>
    api.post<{
      success: boolean;
      tournamentId: string;
      section: string;
      prizeDistribution: any[];
      prizesAwarded?: any[];
      metadata?: any;
      error?: string;
    }>(`/pairings/tournament/${tournamentId}/team-prizes/generate`, {
      prizes,
      section
    }),

  updateResult: (id: string, result: string) =>
    api.put(`/pairings/${id}/result`, { result }),

  // Section management methods
  getSectionStatusSimple: (tournamentId: string, sectionName: string) =>
    api.get<{
      currentRound: number;
      totalRounds: number;
      isComplete: boolean;
      hasIncompleteResults: boolean;
      canGenerateNextRound: boolean;
      canCompleteRound: boolean;
      hasPairings: boolean;
    }>(`/pairings/tournament/${tournamentId}/section/${sectionName}/status`),

  resetSection: (tournamentId: string, sectionName: string) =>
    api.post(`/pairings/tournament/${tournamentId}/section/${sectionName}/reset`),

  generateNextRound: (tournamentId: string, sectionName: string) =>
    api.post(`/pairings/tournament/${tournamentId}/section/${sectionName}/generate-next`),

  // Drag and drop pairing methods
  updatePairingPlayers: (pairingId: string, whitePlayerId: string, blackPlayerId: string, whitePlayer: any, blackPlayer: any) =>
    api.put(`/pairings/pairing/${pairingId}/players`, { whitePlayerId, blackPlayerId, whitePlayer, blackPlayer }),

  createCustomPairing: (tournamentId: string, round: number, section: string, whitePlayer: any, blackPlayer: any) =>
    api.post('/pairings/pairing/custom', { tournamentId, round, section, whitePlayer, blackPlayer }),

  deletePairing: (pairingId: string) =>
    api.delete(`/pairings/pairing/${pairingId}`),

  resetPairings: (tournamentId: string, round: number, sectionName?: string) =>
    api.post(`/pairings/tournament/${tournamentId}/round/${round}/reset`, { sectionName }),

  /**
   * Deal one board's drawbacks again, in a tournament running Drawback Chess
   * over the board. A board otherwise keeps what it was dealt, so that a sheet
   * already on the tables cannot change; this is the deliberate exception.
   */
  rerollDrawbacks: (tournamentId: string, pairingId: string) =>
    api.post<{ success: boolean; drawbacks: { white: PairingDrawback | null; black: PairingDrawback | null } }>(
      `/pairings/tournament/${tournamentId}/pairing/${pairingId}/drawbacks/reroll`
    ),

  /** Deal a whole round's drawbacks again. */
  redealDrawbacks: (tournamentId: string, round: number) =>
    api.post(`/pairings/tournament/${tournamentId}/round/${round}/drawbacks/redeal`),

  updateTournamentStatus: (tournamentId: string, status: string) =>
    api.put(`/tournaments/${tournamentId}/status`, { status }),

  completeRound: (tournamentId: string, round: number, sectionName: string) =>
    api.post(`/pairings/tournament/${tournamentId}/round/${round}/complete`, { sectionName }),

  // Enhanced round status with section breakdown
  getRoundStatus: (tournamentId: string, round: number, section?: string) =>
    api.get<{
      success: boolean;
      tournamentId: string;
      round: number;
      totalPairings: number;
      completedPairings: number;
      incompletePairings: number;
      completionPercentage: number;
      isComplete: boolean;
      hasPairings: boolean;
      canGenerateNextRound: boolean;
      sections: Record<string, {
        total: number;
        completed: number;
        pending: number;
        percentage: number;
        isComplete: boolean;
      }>;
    }>(`/pairings/tournament/${tournamentId}/round/${round}/status${section ? `?section=${section}` : ''}`),

  getStandings: (tournamentId: string, includeRoundResults: boolean = false, showPrizes: boolean = false) =>
    api.get<{ success: boolean, data: Standing[], error?: string }>(`/pairings/tournament/${tournamentId}/standings?includeRoundResults=${includeRoundResults}&showPrizes=${showPrizes}`),

  getPlayerActivePairing: (tournamentId: string, playerId: string, uscfId?: string) => {
    const params = uscfId ? { uscf_id: uscfId } : undefined;
    return api.get<{ success: boolean; data: Pairing | null; error?: string }>(
      `/pairings/tournament/${tournamentId}/player/${playerId}/active`,
      { params }
    );
  },

  // Edit functions
  updateBoardNumber: (pairingId: string, boardNumber: number) =>
    api.put<{ success: boolean, message: string }>(`/pairings/${pairingId}/board-number`, { boardNumber }),

  delete: (pairingId: string) =>
    api.delete<{ success: boolean, message: string }>(`/pairings/${pairingId}`),

  // Import pairings from CSV
  importCSV: (tournamentId: string, round: number, file: File, section?: string, clearExisting: boolean = false) => {
    const formData = new FormData();
    formData.append('csvFile', file);
    formData.append('tournament_id', tournamentId);
    formData.append('round', round.toString());
    if (section) formData.append('section', section);
    formData.append('clear_existing', clearExisting.toString());
    return api.post<{
      success: boolean;
      message?: string;
      error?: string;
      data?: {
        importedCount: number;
        totalRows: number;
        parseErrors?: any[];
        validationErrors?: any[];
        pairings: any[];
      };
    }>('/pairings/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  createManual: (tournamentId: string, sectionName: string, round: number, boardNumber: number) =>
    api.post<{ success: boolean, message: string, pairing: Pairing }>(`/pairings/manual`, {
      tournamentId,
      sectionName,
      round,
      boardNumber
    }),

  createManualWithPlayers: (tournamentId: string, sectionName: string, round: number, boardNumber: number, whitePlayerId: string, blackPlayerId: string) =>
    api.post<{ success: boolean, message: string, pairing: Pairing }>(`/pairings/manual`, {
      tournamentId,
      sectionName,
      round,
      boardNumber,
      whitePlayerId,
      blackPlayerId
    }),

  createTeamManualWithPlayers: (
    tournamentId: string,
    sectionName: string,
    round: number,
    matchTable: number,
    gameNumber: number,
    whitePlayerId: string,
    blackPlayerId: string,
    whiteTeamName: string,
    blackTeamName: string,
    matchName: string
  ) =>
    api.post<{ success: boolean, message: string, pairing: Pairing }>(`/pairings/manual/team`, {
      tournamentId,
      sectionName,
      round,
      matchTable,
      gameNumber,
      whitePlayerId,
      blackPlayerId,
      whiteTeamName,
      blackTeamName,
      matchName
    }),

  // Import WinTD / pasted standings for a round (match roster, verify scores, optional rating sync)
  importStandings: (payload: {
    tournamentId: string;
    round: number;
    section?: string;
    standings?: Array<{
      place?: number | null;
      tied?: boolean;
      name: string;
      rating?: number | null;
      score?: number | null;
      mmed?: number | null;
      med?: number | null;
      solk?: number | null;
      sbx2?: number | null;
    }>;
    text?: string;
    updateRatings?: boolean;
    persist?: boolean;
  }) =>
    api.post<{
      success: boolean;
      round: number;
      section: string;
      totalRows: number;
      matched: number;
      unmatched: number;
      scoreMismatches: number;
      persisted: number;
      results: Array<{
        index: number;
        name: string;
        playerId?: string;
        playerName?: string;
        matched: boolean;
        scoreMatch?: boolean;
        importedScore?: number | null;
        computedScore?: number;
        scoreDelta?: number | null;
      }>;
      errors?: Array<{ index: number; name?: string; error: string }>;
      message?: string;
    }>('/pairings/standings/import', payload),

  getImportedStandings: (tournamentId: string, round?: number, section?: string) =>
    api.get<{ success: boolean; standings: any[] }>(`/pairings/standings/imported/${tournamentId}`, {
      params: { round, section },
    }),

  // Paste bulk pairings
  pastePairings: (tournamentId: string, round: number, pairings: Array<{ board?: number, white: string, black: string | null, section?: string, bye_type?: string }>, clearExisting?: boolean) =>
    api.post<{
      success: boolean;
      message: string;
      created: number;
      errorCount: number;
      results: Array<{
        index: number;
        success: boolean;
        pairingId: string;
        board: number;
        section: string;
        whitePlayerId: string | null;
        blackPlayerId: string | null;
      }>;
      errors?: Array<{
        index: number;
        pairing: any;
        error: string;
      }>;
    }>('/pairings/paste', {
      tournamentId,
      round,
      pairings,
      clearExisting
    }),

  // AI text import for pairings
  processAIPairingImport: (tournamentId: string, text: string) =>
    api.post<{
      success: boolean;
      data: {
        pairings: Array<{ board?: number, white: string, black: string | null, section?: string }>;
        count: number;
      };
      error?: string;
    }>('/pairings/ai-text-import', { tournament_id: tournamentId, text }, {
      timeout: 120000 // 120 second timeout for AI processing
    }),

  swapPairings: (pairingId1: string, pairingId2: string) =>
    api.post<{ success: boolean, message: string }>(`/pairings/swap`, {
      pairingId1,
      pairingId2
    }),

  swapPlayers: (pairingId1: string, pairingId2: string, position1: 'white' | 'black', position2: 'white' | 'black') =>
    api.post<{ success: boolean, message: string }>(`/pairings/swap-players`, {
      pairingId1,
      pairingId2,
      position1,
      position2
    }),

  // Create custom game for a pairing (online tournaments)
  createGameForPairing: (pairingId: string) =>
    api.post<{
      success: boolean;
      message?: string;
      error?: string;
      gameId?: string;
      whiteLink?: string;
      blackLink?: string;
      spectatorLink?: string;
      pairingId?: string;
    }>('/games/create-for-pairing', { pairingId }),
};

// Export API
export const exportApi = {
  exportDBF: (
    tournamentId: string,
    options?: { exportPath?: string; mode?: 'generate' | 'regenerate' }
  ) => {
    const params = new URLSearchParams();
    if (options?.exportPath) {
      params.append('exportPath', options.exportPath);
    }
    if (options?.mode) {
      params.append('mode', options.mode);
    }
    const query = params.toString();
    return api.get(
      `/export/uscf/${tournamentId}${query ? `?${query}` : ''}`
    );
  },
  downloadDBF: (tournamentId: string) =>
    api.get(`/export/uscf/${tournamentId}/download`, { responseType: 'blob' }),
  getExportStatus: (tournamentId: string) =>
    api.get(`/export/uscf/${tournamentId}/status`),
  exportTRF: (tournamentId: string, options?: { round?: number; exportPath?: string }) => {
    const params = new URLSearchParams();
    if (options?.round !== undefined) {
      params.append('round', options.round.toString());
    }
    if (options?.exportPath) {
      params.append('exportPath', options.exportPath);
    }
    const query = params.toString();
    return api.get(`/export/trf/${tournamentId}${query ? `?${query}` : ''}`);
  },
  exportAllTRF: (tournamentId: string, exportPath?: string) => {
    const params = new URLSearchParams();
    if (exportPath) {
      params.append('exportPath', exportPath);
    }
    const query = params.toString();
    return api.get(`/export/trf/${tournamentId}/all-rounds${query ? `?${query}` : ''}`);
  },
  getTRFContent: (tournamentId: string, round: number) =>
    api.get(`/export/trf/${tournamentId}/content?round=${round}`),
  downloadTRF: (tournamentId: string, round: number) =>
    api.get(`/export/trf/${tournamentId}/download?round=${round}`, { responseType: 'blob' }),
  downloadAllTRF: (tournamentId: string) =>
    api.get(`/export/trf/${tournamentId}/download-all`, { responseType: 'blob' }),
  validateTRF: (content: string) =>
    api.post(`/export/trf/validate`, { content }),
};


// Team API
export const teamApi = {
  getTournamentTeams: (tournamentId: string) =>
    api.get<{ success: boolean, teams: any[], error?: string }>(`/teams/tournament/${tournamentId}`),
  getTeamMembers: (tournamentId: string) =>
    api.get<{ success: boolean, members: any[], error?: string }>(`/teams/tournament/${tournamentId}/members`),
  getTeamStandings: (tournamentId: string, scoringMethod?: string, topN?: number) => {
    const params = new URLSearchParams();
    if (scoringMethod) params.append('scoring_method', scoringMethod);
    if (topN) params.append('top_n', topN.toString());
    return api.get<{ success: boolean, standings: any[], standingsBySection?: { [key: string]: any[] }, type: string, scoring_method: string, top_n?: number, error?: string }>(`/teams/tournament/${tournamentId}/standings?${params.toString()}`);
  }
};

// Registration API
export const registrationApi = {
  getTournamentInfo: (tournamentId: string, inviteToken?: string) =>
    api.get<{
      success: boolean, data: {
        id: string;
        name: string;
        format: string;
        rounds: number;
        start_date?: string;
        end_date?: string;
        entry_fee?: number;
        payment_enabled?: boolean;
        payment_settings?: any;
        sections: Array<{
          name: string;
          min_rating?: number;
          max_rating?: number;
          description?: string;
        }>;
        custom_fields?: Array<{
          id?: string;
          label?: string;
          name?: string;  // For backward compatibility
          type: string;
          required: boolean;
          options?: string[];
          placeholder?: string;
        }>;
        registration_form_settings?: any;
        allow_registration?: boolean;
      }, error?: string
    }>(`/registrations/tournament/${tournamentId}/info${inviteToken ? `?invite=${encodeURIComponent(inviteToken)}` : ''}`),
  searchPlayers: (searchTerm: string, limit: number = 10, tournamentId?: string, searchType?: 'both' | 'uscf' | 'fide') =>
    api.get<{
      success: boolean, data: {
        players: Array<{
          name: string;
          memberId: string;
          state: string | null;
          ratings: {
            regular?: number | null;
            quick?: number | null;
            blitz?: number | null;
            online_regular?: number | null;
            online_quick?: number | null;
            online_blitz?: number | null;
            standard?: number | null;
            rapid?: number | null;
          };
          uscf_id?: string;
          fide_id?: string;
          rating: number | null;
          email?: string;
          phone?: string;
          expiration_date?: string;
          source?: 'uscf' | 'fide';
          title?: string | null;
          federation?: string | null;
          birthYear?: number | null;
        }>;
        count: number;
      }, error?: string
    }>(`/registrations/search-players?q=${encodeURIComponent(searchTerm)}&limit=${limit}${tournamentId ? `&tournamentId=${encodeURIComponent(tournamentId)}` : ''}${searchType ? `&searchType=${searchType}` : ''}`),
  submitRegistration: (registrationData: {
    tournament_id: string;
    player_name: string;
    uscf_id?: string;
    rating?: number;
    email: string;
    phone?: string;
    section?: string;
    bye_requests?: number[];
    notes?: string;
    payment_amount?: number;
    payment_method?: string;
    payment_intent_id?: string;
    payment_status?: string;
    custom_fields?: any[];
  }) =>
    api.post<{
      success: boolean, data: {
        registration_id: string;
        player_id: string;
      }, message?: string, error?: string
    }>(`/registrations/submit`, registrationData),
  approveRegistration: (registrationId: string, notes?: string) =>
    api.post<{ success: boolean; message?: string; error?: string }>(
      `/registrations/${registrationId}/approve`,
      { notes: notes || '' }
    ),
  rejectRegistration: (registrationId: string, notes?: string) =>
    api.post<{ success: boolean; message?: string; error?: string }>(
      `/registrations/${registrationId}/reject`,
      { notes: notes || '' }
    ),
  getRegistrationStatus: (registrationId: string) =>
    api.get<{
      success: boolean, data: {
        id: string;
        tournament_id: string;
        player_name: string;
        uscf_id?: string;
        email: string;
        phone?: string;
        section?: string;
        status: string;
        created_at: string;
        tournament_name?: string;
        player_status?: string;
      }, error?: string
    }>(`/registrations/status/${registrationId}`),
};

// User API for profile and API key management
export const userApi = {
  // Get user's API keys
  getApiKeys: (userId: string) =>
    api.get<{ success: boolean, data: any[], error?: string }>(`/users/${userId}/api-keys`),

  // Generate new API key
  generateApiKey: (userId: string, keyData: {
    name: string;
    description: string;
    permissions: string;
    rate_limit: number;
  }) =>
    api.post<{ success: boolean, data: { api_key: string, key_id: string }, error?: string }>(`/users/${userId}/api-key`, keyData),

  // Update API key
  updateApiKey: (userId: string, keyId: string, updates: {
    name?: string;
    description?: string;
    permissions?: string;
    rate_limit?: number;
  }) =>
    api.put<{ success: boolean, error?: string }>(`/users/${userId}/api-keys/${keyId}`, updates),

  // Revoke API key
  revokeApiKey: (userId: string, keyId: string) =>
    api.delete<{ success: boolean, error?: string }>(`/users/${userId}/api-keys/${keyId}`),

  // Get API key statistics
  getApiKeyStats: (userId: string, keyId: string) =>
    api.get<{ success: boolean, data: any, error?: string }>(`/users/${userId}/api-keys/${keyId}/stats`)
};

// Pairing Editor API for advanced editing features
export const pairingEditorApi = {
  // Update a single pairing
  updatePairing: (pairingId: string, updates: Partial<Pairing>) =>
    api.put(`/pairing-editor/pairing/${pairingId}`, updates),

  // Swap players in a pairing
  swapPlayers: (pairingId: string) =>
    api.post(`/pairing-editor/pairing/${pairingId}/swap`),

  // Swap two pairings (exchange board numbers)
  swapPairings: (pairingId1: string, pairingId2: string) =>
    api.post('/pairing-editor/pairings/swap', { pairingId1, pairingId2 }),

  // Move a player from one pairing to another
  movePlayer: (fromPairingId: string, toPairingId: string, position: 'white' | 'black') =>
    api.post('/pairing-editor/pairings/move-player', { fromPairingId, toPairingId, position }),

  // Delete a pairing
  deletePairing: (pairingId: string) =>
    api.delete(`/pairing-editor/pairing/${pairingId}`),

  // Duplicate a pairing
  duplicatePairing: (pairingId: string, newBoard?: number) =>
    api.post(`/pairing-editor/pairing/${pairingId}/duplicate`, { newBoard }),

  // Bulk update pairings
  bulkUpdate: (pairings: Pairing[]) =>
    api.put('/pairing-editor/pairings/bulk', { pairings }),

  // Validate pairings for conflicts
  validatePairings: (pairings: Pairing[], tournamentId: string, round: number) =>
    api.post('/pairing-editor/pairings/validate', { pairings, tournamentId, round }),

  // Get pairing history
  getHistory: (tournamentId: string, round?: number, section?: string) => {
    const params = new URLSearchParams();
    if (round) params.append('round', round.toString());
    if (section) params.append('section', section);
    return api.get(`/pairing-editor/tournament/${tournamentId}/history?${params.toString()}`);
  }
};

// SMS API
export const smsApi = {
  // Send single SMS
  sendSMS: (phoneNumber: string, message: string, options?: any) =>
    api.post('/sms/send', { phoneNumber, message, options }),

  // Send bulk SMS
  sendBulkSMS: (recipients: Array<{ phoneNumber: string, message: string, playerName: string }>, options?: any) =>
    api.post('/sms/bulk', { recipients, options }),

  // Send tournament notification
  sendTournamentNotification: (tournamentId: string, notificationType: string, data?: any) =>
    api.post(`/sms/tournament/${tournamentId}/notify`, { notificationType, data }),

  // Get SMS delivery status
  getDeliveryStatus: (messageId: string) =>
    api.get(`/sms/status/${messageId}`),

  // Get SMS usage statistics
  getStats: (startDate: string, endDate: string) =>
    api.get(`/sms/stats?startDate=${startDate}&endDate=${endDate}`),

  // Test SMS configuration
  testSMS: (phoneNumber: string) =>
    api.post('/sms/test', { phoneNumber }),

  // Get SMS configuration status
  getConfig: () =>
    api.get('/sms/config')
};

// QR Code API
export const qrCodeApi = {
  // Generate pairings QR code
  generatePairings: (tournamentId: string, round: number, options?: any) =>
    api.post('/qr-codes/pairings', { tournamentId, round, options }),

  // Generate standings QR code
  generateStandings: (tournamentId: string, options?: any) =>
    api.post('/qr-codes/standings', { tournamentId, options }),

  // Generate tournament QR code
  generateTournament: (tournamentId: string, options?: any) =>
    api.post('/qr-codes/tournament', { tournamentId, options }),

  // Generate player check-in QR code
  generatePlayerCheckIn: (tournamentId: string, playerId: string, options?: any) =>
    api.post('/qr-codes/player-checkin', { tournamentId, playerId, options }),

  // Generate tournament check-in poster QR code
  generateTournamentCheckIn: (tournamentId: string, options?: any) =>
    api.post('/qr-codes/tournament-checkin', { tournamentId, options }),

  // Generate custom QR code
  generateCustom: (content: string, options?: any) =>
    api.post('/qr-codes/custom', { content, options }),

  // Generate batch QR codes
  generateTournamentBatch: (tournamentId: string, options?: any) =>
    api.post('/qr-codes/tournament-batch', { tournamentId, options }),

  // Generate player QR codes
  generatePlayersBatch: (tournamentId: string, players: any[], options?: any) =>
    api.post('/qr-codes/players-batch', { tournamentId, players, options }),

  // Generate print-ready QR code
  generatePrint: (content: string, options?: any) =>
    api.post('/qr-codes/print', { content, options }),

  // Validate QR code content
  validateContent: (content: string) =>
    api.post('/qr-codes/validate', { content }),

  // Get available options
  getOptions: () =>
    api.get('/qr-codes/options')
};

// Player Profile API
export const playerProfileApi = {
  // Get player profile
  getProfile: (playerId: string) =>
    api.get(`/player-profiles/${playerId}`),

  // Get player by USCF ID (for player login)
  getByUscfId: (uscfId: string) =>
    api.get(`/player-profiles/uscf/${uscfId}`),

  // Public display profile (avatar + linked accounts) for tournament pages
  getPublicDisplay: (uscfId: string) =>
    api.get(`/player-profiles/uscf/${encodeURIComponent(uscfId)}/public-display`),

  getPublicDisplayBatch: (uscfIds: string[]) =>
    api.post('/player-profiles/public-display/batch', { uscfIds }),

  // Get player's tournaments by US Chess ID (use "me" or omit client USCF — server uses linked profile).
  getTournaments: (
    uscfId: string,
    opts?: { bearerToken?: string | null }
  ) => {
    const segment = encodeURIComponent(uscfId || 'me').replace(/\(/g, '%28').replace(/\)/g, '%29');
    const bearer =
      opts?.bearerToken ||
      (() => {
        try {
          const stored = localStorage.getItem('paircraft_player_auth');
          const parsed = stored ? JSON.parse(stored) : null;
          return parsed?.token as string | undefined;
        } catch {
          return undefined;
        }
      })();
    return api.get(`/player-profiles/uscf/${segment}/tournaments`, {
      headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
    });
  },

  // Update player profile
  updateProfile: (playerId: string, updates: any) =>
    api.put(`/player-profiles/${playerId}`, updates),

  // Update player's Chess.com and Lichess usernames
  updateAccounts: (uscfId: string, accounts: { lichessUsername?: string; chesscomUsername?: string }) =>
    api.put(`/player-profiles/uscf/${uscfId}/accounts`, accounts),

  // Upload player photo
  uploadPhoto: (playerId: string, formData: FormData) =>
    api.post(`/player-profiles/${playerId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Delete player photo
  deletePhoto: (playerId: string, photoType: string) =>
    api.delete(`/player-profiles/${playerId}/photo/${photoType}`),

  // Get player statistics
  getStatistics: (playerId: string) =>
    api.get(`/player-profiles/${playerId}/statistics`),

  // Get player tournament history
  getTournamentHistory: (playerId: string, options?: any) =>
    api.get(`/player-profiles/${playerId}/tournaments`, { params: options }),

  // Get player achievements
  getAchievements: (playerId: string, type?: string) =>
    api.get(`/player-profiles/${playerId}/achievements`, { params: { type } }),

  // Add achievement
  addAchievement: (playerId: string, achievement: any) =>
    api.post(`/player-profiles/${playerId}/achievements`, achievement),

  // Search players
  searchPlayers: (query: string, options?: any) =>
    api.get('/player-profiles/search', { params: { q: query, ...options } }),

  // Get leaderboard
  getLeaderboard: (options?: any) =>
    api.get('/player-profiles/leaderboard', { params: options }),

  // Get achievement types
  getAchievementTypes: () =>
    api.get('/player-profiles/achievements/types')
};

// Live Standings API
export const liveStandingsApi = {
  // Get current live standings
  getStandings: (tournamentId: string) =>
    api.get(`/live-standings/${tournamentId}`),

  // Trigger manual update
  triggerUpdate: (tournamentId: string) =>
    api.post(`/live-standings/${tournamentId}/trigger`),

  // Get connected clients count
  getClientCount: (tournamentId: string) =>
    api.get(`/live-standings/${tournamentId}/clients`),

  // Get all active tournaments
  getActiveTournaments: () =>
    api.get('/live-standings/active'),

  // Update all active tournaments
  updateAllTournaments: () =>
    api.post('/live-standings/update-all'),

  // Get service statistics
  getStats: () =>
    api.get('/live-standings/stats'),

  // Cleanup inactive connections
  cleanup: () =>
    api.post('/live-standings/cleanup'),

  // Get WebSocket connection info
  getWSInfo: () =>
    api.get('/live-standings/ws-info')
};

// Payment API
export const paymentApi = {
  // Process entry fee payment
  processEntryFee: (paymentData: any) =>
    api.post('/payments/entry-fee', paymentData),

  // Confirm payment
  confirmPayment: (confirmationData: any) =>
    api.post('/payments/confirm', confirmationData),

  // Process prize payments
  processPrizes: (prizeData: any) =>
    api.post('/payments/prizes', prizeData),

  // Get payment history
  getHistory: (tournamentId: string) =>
    api.get(`/payments/history/${tournamentId}`),

  // Get payment statistics
  getStats: (tournamentId: string) =>
    api.get(`/payments/stats/${tournamentId}`),

  // Refund payment
  refundPayment: (paymentId: string, amount?: number) =>
    api.post('/payments/refund', { paymentId, amount }),

  // Get payment configuration
  getConfig: () =>
    api.get('/payments/config'),

  // Get available payment methods
  getMethods: () =>
    api.get('/payments/methods'),

  // Stripe specific endpoints
  createStripeIntent: (intentData: any) =>
    api.post('/payments/stripe/create-intent', intentData),

  // PayPal specific endpoints
  createPayPalOrder: (orderData: any) =>
    api.post('/payments/paypal/create-order', orderData),

  capturePayPalOrder: (orderId: string) =>
    api.post('/payments/paypal/capture-order', { orderId }),

  // Player prize payout endpoints
  getPlayerPrizes: () =>
    api.get('/payments/player/prizes'),

  getPlayerPaymentMethods: () =>
    api.get('/payments/player/payment-methods'),

  savePlayerPaymentMethod: (methodData: {
    payment_provider: 'paypal' | 'stripe' | 'venmo';
    account_identifier: string;
    account_name?: string;
    is_primary?: boolean;
  }) =>
    api.post('/payments/player/payment-methods', methodData),

  requestPrizePayout: (payoutData: {
    prize_distribution_id: string;
    payment_method_id: string;
  }) =>
    api.post('/payments/player/request-payout', payoutData),

  getPlayerPayouts: () =>
    api.get('/payments/player/payouts')
};

export const playerWalletApi = {
  getWallet: () =>
    api.get<{ success: boolean; data: any; error?: string }>('/player-wallet'),
  syncPrizes: () =>
    api.post<{ success: boolean; message?: string; data: any; error?: string }>('/player-wallet/sync-prizes'),
  getPayables: () =>
    api.get<{ success: boolean; data: { payables: any[] }; error?: string }>('/player-wallet/payables'),
  payDues: (duesId: string) =>
    api.post<{ success: boolean; message?: string; data: any; error?: string }>(`/player-wallet/pay/dues/${duesId}`),
  payRegistration: (registrationId: string) =>
    api.post<{ success: boolean; message?: string; data: any; error?: string }>(`/player-wallet/pay/registration/${registrationId}`),
  startStripeConnect: (data?: { returnUrl?: string; refreshUrl?: string }) =>
    api.post<{ success: boolean; data: { url: string }; error?: string }>('/player-wallet/stripe/connect', data || {}),
  refreshStripeStatus: () =>
    api.post<{ success: boolean; data: any; error?: string }>('/player-wallet/stripe/refresh-status'),
  listWithdrawalRequests: () =>
    api.get<{ success: boolean; data: { requests: any[] }; error?: string }>('/player-wallet/withdrawal-requests'),
  createWithdrawalRequest: (data: {
    organizationId: string;
    amountCents: number;
    payoutMethod: string;
    payoutDetails?: Record<string, string>;
    playerNotes?: string;
  }) =>
    api.post<{ success: boolean; message?: string; data: any; error?: string }>('/player-wallet/withdrawal-requests', data),
  cancelWithdrawalRequest: (id: string) =>
    api.post<{ success: boolean; data: any; error?: string }>(`/player-wallet/withdrawal-requests/${id}/cancel`),
  listOrgWithdrawalRequests: (organizationId: string, status?: string) =>
    api.get<{ success: boolean; data: { requests: any[] }; error?: string }>(
      `/player-wallet/org/${organizationId}/withdrawal-requests`,
      { params: status ? { status } : {} }
    ),
  fulfillWithdrawalRequest: (organizationId: string, id: string, data?: { adminNotes?: string }) =>
    api.post<{ success: boolean; message?: string; data: any; error?: string }>(
      `/player-wallet/org/${organizationId}/withdrawal-requests/${id}/fulfill`,
      data || {}
    ),
  rejectWithdrawalRequest: (organizationId: string, id: string, data?: { adminNotes?: string }) =>
    api.post<{ success: boolean; message?: string; data: any; error?: string }>(
      `/player-wallet/org/${organizationId}/withdrawal-requests/${id}/reject`,
      data || {}
    ),
  issueGiftCredit: (data: { userId: string; amountCents: number; organizationId: string; note?: string }) =>
    api.post<{ success: boolean; message?: string; error?: string }>('/player-wallet/gift-credit', data),
};

// Analytics API
export const analyticsApi = {
  // Get comprehensive tournament analytics
  getTournamentAnalytics: (tournamentId: string) =>
    api.get(`/analytics/tournament/${tournamentId}`),

  // Get specific analytics sections
  getTournamentOverview: (tournamentId: string) =>
    api.get(`/analytics/tournament/${tournamentId}/overview`),

  getPlayerAnalytics: (tournamentId: string) =>
    api.get(`/analytics/tournament/${tournamentId}/players`),

  getPairingAnalytics: (tournamentId: string) =>
    api.get(`/analytics/tournament/${tournamentId}/pairings`),

  getRatingAnalytics: (tournamentId: string) =>
    api.get(`/analytics/tournament/${tournamentId}/ratings`),

  getPerformanceAnalytics: (tournamentId: string) =>
    api.get(`/analytics/tournament/${tournamentId}/performance`),

  getTimeAnalytics: (tournamentId: string) =>
    api.get(`/analytics/tournament/${tournamentId}/time`),

  getSectionAnalytics: (tournamentId: string) =>
    api.get(`/analytics/tournament/${tournamentId}/sections`),

  getFinancialAnalytics: (tournamentId: string) =>
    api.get(`/analytics/tournament/${tournamentId}/financial`),

  // System-wide analytics
  getSystemAnalytics: () =>
    api.get('/analytics/system'),

  getTournamentTrends: () =>
    api.get('/analytics/trends/tournaments'),

  getPlayerTrends: () =>
    api.get('/analytics/trends/players'),

  getSystemMetrics: () =>
    api.get('/analytics/metrics'),

  getFeatureAnalytics: () =>
    api.get('/analytics/features'),

  // Dashboard and export
  getDashboardData: (tournamentId: string, widgets: string[] = []) =>
    api.get(`/analytics/dashboard/${tournamentId}`, { params: { widgets: widgets.join(',') } }),

  exportAnalytics: (tournamentId: string, format: 'json' | 'csv' = 'json') =>
    api.get(`/analytics/export/${tournamentId}`, { params: { format } }),

  // Cache management
  clearAnalyticsCache: () =>
    api.post('/analytics/clear-cache')
};

// Career Analytics API
export const careerApi = {
  // Unified data from all sources
  getUnifiedData: (params: { lichessUsername?: string; chesscomUsername?: string; uscfId?: string; timeClass?: string; color?: string; maxGames?: number }) =>
    api.get('/career/unified-data', { params }),

  // Rating trajectory
  getRatingTrajectory: (params: { lichessUsername?: string; chesscomUsername?: string; uscfId?: string }) =>
    api.get('/career/rating-trajectory', { params }),

  // Milestones
  getMilestones: (params: { lichessUsername?: string; chesscomUsername?: string; uscfId?: string }) =>
    api.get('/career/milestones', { params }),

  // Growth rate
  getGrowthRate: (params: { lichessUsername?: string; chesscomUsername?: string; uscfId?: string; period?: 'month' | 'quarter' }) =>
    api.get('/career/growth-rate', { params }),

  // Opening repertoire
  getOpeningRepertoire: (params: { lichessUsername?: string; chesscomUsername?: string; uscfId?: string; color?: string }) =>
    api.get('/career/opening-repertoire', { params }),

  // Percentiles
  getPercentiles: (params: { lichessUsername?: string; chesscomUsername?: string; uscfId?: string; tournamentId?: string; section?: string; metric?: string }) =>
    api.get('/career/percentiles', { params }),

  // Predictive analytics
  getPredictive: (params: { tournamentId: string; playerId: string; round?: number }) =>
    api.get('/career/predictive', { params }),

  // Phase performance
  getPhasePerformance: (params: { lichessUsername?: string; chesscomUsername?: string; uscfId?: string }) =>
    api.get('/career/phase-performance', { params }),

  // Head-to-head
  getHeadToHead: (params: { playerId: string; opponentId: string; tournamentId?: string }) =>
    api.get('/career/head-to-head', { params }),

  // Tournament benchmarking
  getTournamentBenchmark: (params: { tournamentId: string; playerId?: string }) =>
    api.get('/career/tournament-benchmark', { params }),

  // Accuracy trends
  getAccuracyTrends: (params: { lichessUsername?: string; chesscomUsername?: string; uscfId?: string }) =>
    api.get('/career/accuracy-trends', { params }),

  // Norms & title progress
  getNorms: (params: { lichessUsername?: string; chesscomUsername?: string; uscfId?: string }) =>
    api.get('/career/norms', { params })
};

// Chess Platform Integration API
export const chessIntegrationApi = {
  // Chess.com endpoints
  getChessComPlayer: (username: string) =>
    api.get(`/chess/chesscom/player/${username}`),

  getChessComPlayerStats: (username: string) =>
    api.get(`/chess/chesscom/player/${username}/stats`),

  getChessComPlayerGames: (username: string, limit: number = 10) =>
    api.get(`/chess/chesscom/player/${username}/games`, { params: { limit } }),

  searchChessComPlayers: (query: string) =>
    api.get('/chess/chesscom/search', { params: { q: query } }),

  getChessComTournament: (tournamentId: string) =>
    api.get(`/chess/chesscom/tournament/${tournamentId}`),

  getChessComLiveGames: () =>
    api.get('/chess/chesscom/live-games'),

  getChessComPuzzle: () =>
    api.get('/chess/chesscom/puzzle'),

  getChessComClub: (clubId: string) =>
    api.get(`/chess/chesscom/club/${clubId}`),

  // Lichess endpoints
  getLichessPlayer: (username: string) =>
    api.get(`/chess/lichess/player/${username}`),

  getLichessPlayerStats: (username: string) =>
    api.get(`/chess/lichess/player/${username}/stats`),

  getLichessPlayerGames: (username: string, limit: number = 10) =>
    api.get(`/chess/lichess/player/${username}/games`, { params: { limit } }),

  searchLichessPlayers: (query: string) =>
    api.get('/chess/lichess/search', { params: { q: query } }),

  getLichessTournament: (tournamentId: string) =>
    api.get(`/chess/lichess/tournament/${tournamentId}`),

  getLichessLiveGames: () =>
    api.get('/chess/lichess/live-games'),

  getLichessPuzzle: () =>
    api.get('/chess/lichess/puzzle'),

  getLichessTeam: (teamId: string) =>
    api.get(`/chess/lichess/team/${teamId}`),

  getLichessTV: () =>
    api.get('/chess/lichess/tv'),

  // Multi-platform endpoints
  searchPlayers: (query: string, platform?: 'chesscom' | 'lichess') =>
    api.get('/chess/search', { params: { q: query, platform } }),

  getPlayerProfile: (username: string, platform?: 'chesscom' | 'lichess') =>
    api.get(`/chess/player/${username}`, { params: { platform } }),

  getStatus: () =>
    api.get('/chess/status'),

  importPlayer: (username: string, platform: 'chesscom' | 'lichess', tournamentId: string) =>
    api.post('/chess/import-player', { username, platform, tournamentId }),

  getLeaderboards: (platform?: 'chesscom' | 'lichess', timeControl?: string) =>
    api.get('/chess/leaderboards', { params: { platform, timeControl } })
};

// Custom Pages API
export const customPagesApi = {
  getAll: (tournamentId: string) =>
    api.get(`/custom-pages/tournament/${tournamentId}`),

  getOne: (id: string) =>
    api.get(`/custom-pages/${id}`),

  create: (data: any) =>
    api.post('/custom-pages', data),

  update: (id: string, data: any) =>
    api.put(`/custom-pages/${id}`, data),

  delete: (id: string) =>
    api.delete(`/custom-pages/${id}`)
};

// Enhanced Tournament Management API
export const enhancedTournamentApi = {
  // Re-entry system
  executeReEntry: (tournamentId: string, data: {
    playerId: string;
    fromSection?: string;
    toSection: string;
    reentryRound: number;
    notes?: string;
  }) => api.post(`/tournaments/enhanced/${tournamentId}/reentry`, data),

  getReEntryEligiblePlayers: (tournamentId: string, section: string, round: number) =>
    api.get(`/tournaments/enhanced/${tournamentId}/reentry/players/${section}/${round}`),

  validateReEntryPairing: (tournamentId: string, data: {
    playerId: string;
    opponentId: string;
    section: string;
    round: number;
  }) => api.post(`/tournaments/enhanced/${tournamentId}/reentry/validate-pairing`, data),

  getReEntryHistory: (tournamentId: string, playerId?: string) =>
    api.get(`/tournaments/enhanced/${tournamentId}/reentry/history`,
      playerId ? { params: { playerId } } : {}),

  // Enhanced tournament status
  getEnhancedStatus: (tournamentId: string) =>
    api.get(`/tournaments/enhanced/${tournamentId}/status`)
};

// PGN API - Portable Game Notation management
export const pgnApi = {
  // Save PGN for a completed game
  savePGN: (gameData: {
    gameId: string;
    tournamentId: string;
    pairingId?: string;
    pgn: string;
    result?: string;
    whiteName?: string;
    blackName?: string;
    whiteRating?: number | string;
    blackRating?: number | string;
    timeControl?: string;
    round?: number;
    event?: string;
    site?: string;
  }) =>
    api.post<{ success: boolean, message: string, gameId: string, error?: string }>('/pgn/save', gameData),

  // Get PGN for a specific game
  getGamePGN: (gameId: string) =>
    api.get<{ success: boolean, data: any, error?: string }>(`/pgn/game/${gameId}`),

  // Get all PGNs for a tournament
  getTournamentPGNs: (tournamentId: string) =>
    api.get<{ success: boolean, count: number, data: any[], error?: string }>(`/pgn/tournament/${tournamentId}`),

  // Get PGNs for a specific round
  getRoundPGNs: (tournamentId: string, round: number) =>
    api.get<{ success: boolean, count: number, data: any[], error?: string }>(`/pgn/tournament/${tournamentId}/round/${round}`),

  // Export all tournament PGNs as a file
  exportTournamentPGN: (tournamentId: string) =>
    api.get(`/pgn/tournament/${tournamentId}/export`, { responseType: 'blob' }),

  // Get game statistics for a tournament
  getTournamentGameStats: (tournamentId: string) =>
    api.get<{
      success: boolean, data: {
        total_games: number;
        completed_games: number;
        in_progress_games: number;
        abandoned_games: number;
        white_wins: number;
        black_wins: number;
        draws: number;
        games_with_pgn: number;
      }, error?: string
    }>(`/pgn/tournament/${tournamentId}/stats`),

  // Update game status
  updateGameStatus: (gameId: string, status: 'in_progress' | 'completed' | 'abandoned', result?: string) =>
    api.put<{ success: boolean, message: string, gameId: string, status: string, error?: string }>(`/pgn/game/${gameId}/status`, { status, result }),

  // Generate PGN from moves and metadata
  generatePGN: (gameData: {
    whiteName?: string;
    blackName?: string;
    moves?: string[];
    result?: string;
    whiteRating?: number | string;
    blackRating?: number | string;
    event?: string;
    round?: number | string;
    site?: string;
    date?: string;
    timeControl?: string;
    fen?: string;
  }) =>
    api.post<{ success: boolean, pgn: string, error?: string }>('/pgn/generate', gameData),

  // Parse PGN to extract game data
  parsePGN: (pgn: string) =>
    api.post<{
      success: boolean, data: {
        headers: Record<string, string>;
        moves: string[];
        moveCount: number;
      }, error?: string
    }>('/pgn/parse', { pgn }),

  // Get all games across all tournaments
  getAllGames: (
    limit: number = 100,
    offset: number = 0,
    sortBy: string = 'created_at',
    order: string = 'DESC',
    filters?: { uscfId?: string }
  ) =>
    api.get<{ success: boolean, count: number, total: number, limit: number, offset: number, data: any[], error?: string }>('/pgn/all-games', {
      params: { limit, offset, sortBy, order, ...(filters?.uscfId ? { uscfId: filters.uscfId } : {}) }
    }),

  // Get statistics for all games
  getAllGamesStats: () =>
    api.get<{
      success: boolean, data: {
        total_games: number;
        completed_games: number;
        in_progress_games: number;
        abandoned_games: number;
        white_wins: number;
        black_wins: number;
        draws: number;
        total_tournaments: number;
        unique_white_players: number;
        unique_black_players: number;
      }, error?: string
    }>('/pgn/all-games/stats'),

  // Search games globally
  searchGames: (query: string, limit: number = 50) =>
    api.get<{ success: boolean, count: number, data: any[], error?: string }>('/pgn/search', {
      params: { q: query, limit }
    }),

  // Get games for a specific player by USCF ID
  getPlayerGames: (uscfId: string, limit: number = 100, offset: number = 0) =>
    api.get<{
      success: boolean;
      count: number;
      games: any[];
      error?: string;
    }>(`/pgn/player/${uscfId}/games`, { params: { limit, offset } }),

  // Get analyzed games with eval data for a player
  getAnalyzedGames: (identifier: string, limit: number = 100, withAnalysis: boolean = true) =>
    api.get<{
      success: boolean;
      count: number;
      analyzedCount: number;
      summary: {
        totalBlunders: number;
        totalMistakes: number;
        avgCpl: number;
      };
      games: Array<{
        id: string;
        pgn: string;
        result: string;
        white_player_name: string;
        black_player_name: string;
        white_rating?: number;
        black_rating?: number;
        date?: string;
        hasAnalysis: boolean;
        accuracy: {
          blunders: number;
          mistakes: number;
          inaccuracies: number;
          avgCpl: number;
        };
        analysis?: {
          headers: any;
          positions: Array<{
            move_number: number;
            player: string;
            played: { san: string; uci: string };
            classification: string;
            was_best: boolean;
            position_eval: {
              display: string;
              status: string;
              type: string;
              value: number;
            };
            best_move: string;
            fen: string;
          }>;
        };
      }>;
      error?: string;
    }>(`/pgn/player/${identifier}/analyzed-games`, { params: { limit, withAnalysis } }),

  // Fast recent USCF games for player home (single API page, no full history download)
  getExternalPlayerRecentGames: (uscfId: string, limit: number = 5) =>
    api.get<{
      success: boolean;
      status?: 'completed' | 'error';
      player?: { uscf_id: string; name: string; rating?: number; state?: string };
      stats?: { total_games: number; wins: number; losses: number; draws: number; tournaments_played: number } | null;
      games?: Array<{
        tournament_name: string;
        tournament_url?: string | null;
        round: number | null;
        result: string;
        opponent_name: string;
        opponent_uscf_id?: string | null;
        opponent_rating?: number | null;
        color: string;
        player_name: string;
        player_uscf_id: string;
        date?: string;
        year?: string;
      }>;
      total?: number;
      error?: string;
    }>(`/pgn/player-external/${uscfId}/recent`, { params: { limit }, timeout: 30000 }),

  // Get player data from US Chess website (external scraping)
  // Uses longer timeout since GitHub Actions workflow takes ~2 minutes
  getExternalPlayerData: (uscfId: string) =>
    api.get<{
      success: boolean;
      status?: 'pending' | 'completed' | 'error'; // For polling system
      message?: string; // Status message for pending/error states
      player?: { uscf_id: string; name: string; rating?: number; state?: string };
      stats?: { total_games: number; wins: number; losses: number; draws: number; tournaments_played: number };
      games?: Array<{
        tournament_name: string;
        tournament_url?: string;
        round: number;
        result: string;
        opponent_name: string;
        opponent_uscf_id?: string;
        opponent_rating?: number;
        color: string;
        player_name: string;
        player_uscf_id: string;
      }>;
      tournaments?: Array<{ name: string; url: string }>;
      total?: number;
      error?: string;
    }>(`/pgn/player-external/${uscfId}`, { timeout: 180000 }), // 3 minute timeout for GitHub Actions workflow

  // Parse opponent ratings from US Chess API standings
  // Accepts JSON with tournament data, extracts tournament ID and section, then fetches and parses opponent ratings
  parseOpponentRatings: (jsonData: any) =>
    api.post<{
      success: boolean;
      data?: {
        totalEvents: number;
        successfulEvents: number;
        totalPlayers: number;
        results: Array<{
          eventId: string;
          section: number;
          players: Array<{
            memberId: string;
            playerId: string;
            firstName: string;
            lastName: string;
            fullName: string;
            pairingNumber: number;
            ordinal: number;
            score: number;
            preRating?: number;
            ratings: any[];
            roundOutcomes: Array<{
              id: string;
              roundNumber: number;
              outcome: string;
              color: string;
              opponentMemberId?: string;
              opponentFirstName?: string;
              opponentLastName?: string;
              opponentPreRating?: number;
              opponentPostRating?: number;
              opponentFullName?: string;
            }>;
          }>;
          totalPlayers: number;
          success: boolean;
        }>;
      };
      error?: string;
    }>('/pgn/parse-opponent-ratings', jsonData, { timeout: 300000 }), // 5 minute timeout

  // Upload scoresheet image and transcribe to PGN
  uploadScoresheet: (formData: FormData) =>
    api.post<{
      success: boolean;
      message?: string;
      data?: {
        imageUrl: string;
        pgn: string;
        result: string;
        whitePlayer: string;
        blackPlayer: string;
        gameId: string;
        pairingId: string;
      };
      error?: string;
    }>('/pgn/scoresheet-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 180000, // 3 minute timeout for AI transcription
    }),

  // Fetch PGN for a single Lichess game by ID (e.g. "abcd1234")
  getLichessGamePgn: (gameId: string) =>
    api.get<{ success: boolean; pgn?: string; error?: string }>(
      `/lichess/game/${encodeURIComponent(gameId)}/pgn`
    ),

  // Fetch PGN for a single Chess.com game by URL
  getChessComGamePgn: (gameUrl: string) =>
    api.get<{ success: boolean; pgn?: string; error?: string }>(
      `/chesscom/game/pgn`,
      { params: { url: gameUrl } }
    ),

  // Get Chess.com games for a username
  getChessComGames: (username: string) =>
    api.get<{
      games?: Array<{
        url: string;
        pgn: string;
        timeClass: string;
        timeControl: string;
        endTime: number;
        playerColor: 'white' | 'black';
        result: 'win' | 'loss' | 'draw';
        playerRating: number;
        opponentRating: number;
        opponentUsername: string;
        opening: string;
      }>;
      message?: string;
    }>(`/chesscom/games/${encodeURIComponent(username)}`),

  // Get Lichess games for a username
  getLichessGames: (username: string, limit?: number) =>
    api.get<{
      username: string;
      totalGames: number;
      games?: Array<{
        url: string;
        pgn: string;
        timeClass: string;
        timeControl: string;
        endTime?: number;
        playerColor: 'white' | 'black';
        result: 'win' | 'loss' | 'draw';
        playerRating: number;
        opponentRating: number;
        opponentUsername: string;
        opening: string;
      }>;
      error?: string;
    }>(`/lichess/games/${encodeURIComponent(username)}`, {
      params: limit ? { limit } : undefined
    })
};

// Export api as both default and named export for compatibility
// Admin API for superuser operations
export const adminApi = {
  // Create a beta account with exclusive organization
  createBetaAccount: (data: { email: string; username: string; orgName: string }) =>
    api.post<{
      success: boolean;
      data?: {
        userId: string;
        email: string;
        password: string;
        username: string;
        orgId: string;
        orgName: string;
        orgSlug: string;
      };
      error?: string;
    }>('/admin/create-beta-account', data),

  // List all beta accounts
  listBetaAccounts: () =>
    api.get<{
      success: boolean;
      data: Array<{
        id: string;
        email: string;
        username: string;
        plan_tier: string;
        created_at: string;
        organization?: {
          id: string;
          name: string;
          slug: string;
        };
      }>;
      error?: string;
    }>('/admin/beta-accounts'),

  // Revoke beta account (downgrade to free)
  revokeBetaAccount: (userId: string) =>
    api.post<{ success: boolean; error?: string }>(`/admin/beta-accounts/${userId}/revoke`),

  // Get admin dashboard stats
  getAdminStats: () =>
    api.get<{
      success: boolean;
      data: {
        totalUsers: number;
        totalOrganizations: number;
        totalTournaments: number;
        betaAccounts: number;
        organizerAccounts: number;
        freeAccounts: number;
      };
      error?: string;
    }>('/admin/stats')
};

// Social Features API
export const socialApi = {
  // Player account auth/profile completion
  signupPlayerAccount: (payload: { username: string; email: string; password: string; org?: string }) =>
    api.post<{
      success: boolean;
      data?: {
        token: string;
        user: any;
        playerProfile?: {
          uscfId?: string | null;
          phone?: string | null;
          profileComplete?: boolean;
        };
      };
      error?: string;
    }>('/social/player-account/signup', payload),
  getPlayerAccountProfile: (token: string) =>
    api.get<{
      success: boolean;
      profile?: {
        userId: string;
        username: string;
        email: string;
        firstName: string;
        lastName: string;
        phone: string;
        uscfId: string;
        fideId?: string;
        lichessUsername?: string;
        chesscomUsername?: string;
        hideLichessPublic?: boolean;
        hideChesscomPublic?: boolean;
        avatarUrl?: string;
        pairingUsername?: string;
        profileComplete: boolean;
      };
      error?: string;
    }>('/social/player-account/profile', {
      headers: { Authorization: `Bearer ${token}` }
    }),
  updatePlayerProfileVisibility: (
    token: string,
    payload: { hideLichessPublic?: boolean; hideChesscomPublic?: boolean }
  ) =>
    api.put<{
      success: boolean;
      profile?: { hideLichessPublic: boolean; hideChesscomPublic: boolean };
      error?: string;
    }>('/social/player-account/profile/visibility', payload, {
      headers: { Authorization: `Bearer ${token}` }
    }),
  updatePlayerAccountProfile: (
    token: string,
    payload: { phone?: string; uscfId?: string; noUscfId?: boolean; firstName?: string; lastName?: string; fideId?: string }
  ) =>
    api.put<{
      success: boolean;
      profile?: {
        userId: string;
        username: string;
        email: string;
        firstName: string;
        lastName: string;
        phone: string;
        uscfId: string;
        pairingUsername?: string;
        fideId: string;
        profileComplete: boolean;
      };
      error?: string;
    }>('/social/player-account/profile', payload, {
      headers: { Authorization: `Bearer ${token}` }
    }),
  changePlayerPassword: (token: string, oldPassword: string, newPassword: string) =>
    api.put<{ success: boolean; error?: string }>(
      '/social/player-account/change-password',
      { oldPassword, newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    ),
  changePlayerEmail: (token: string, newEmail: string) =>
    api.put<{ success: boolean; email?: string; error?: string }>(
      '/social/player-account/change-email',
      { newEmail },
      { headers: { Authorization: `Bearer ${token}` } }
    ),

  // Profile
  getProfile: (userId: string) =>
    api.get<{ success: boolean; profile: any }>(`/social/profile/${userId}`),
  updateProfile: (userId: string, updates: any) =>
    api.put<{ success: boolean }>(`/social/profile/${userId}`, updates),

  // Following
  follow: (followerId: string, followingId: string) =>
    api.post<{ success: boolean }>('/social/follow', { followerId, followingId }),
  unfollow: (followerId: string, followingId: string) =>
    api.post<{ success: boolean }>('/social/unfollow', { followerId, followingId }),
  getFollowers: (profileId: string, page?: number, limit?: number) =>
    api.get<{ success: boolean; followers: any[]; total: number }>(`/social/followers/${profileId}?page=${page || 1}&limit=${limit || 20}`),
  getFollowing: (profileId: string, page?: number, limit?: number) =>
    api.get<{ success: boolean; following: any[]; total: number }>(`/social/following/${profileId}?page=${page || 1}&limit=${limit || 20}`),
  isFollowing: (followerId: string, followingId: string) =>
    api.get<{ success: boolean; isFollowing: boolean }>(`/social/is-following/${followerId}/${followingId}`),

  // Messaging
  getConversations: (profileId: string) =>
    api.get<{ success: boolean; conversations: any[] }>(`/social/conversations/${profileId}`),
  getMessages: (conversationId: string, page?: number) =>
    api.get<{ success: boolean; messages: any[] }>(`/social/messages/${conversationId}?page=${page || 1}`),
  sendMessage: (senderId: string, recipientId: string, content: string) =>
    api.post<{ success: boolean; messageId: string; conversationId: string }>('/social/messages', { senderId, recipientId, content }),
  markMessagesRead: (conversationId: string, profileId: string) =>
    api.post<{ success: boolean }>('/social/messages/mark-read', { conversationId, profileId }),

  // Forums
  getThreads: (params: { tournamentId?: string; organizationId?: string; page?: number; type?: string }) =>
    api.get<{ success: boolean; threads: any[] }>('/social/forums/threads', { params }),
  getThread: (threadId: string) =>
    api.get<{ success: boolean; thread: any; replies: any[] }>(`/social/forums/threads/${threadId}`),
  createThread: (data: { authorId: string; tournamentId?: string; organizationId?: string; title: string; content: string; threadType?: string }) =>
    api.post<{ success: boolean; threadId: string }>('/social/forums/threads', data),
  createReply: (data: { threadId: string; authorId: string; content: string; parentReplyId?: string }) =>
    api.post<{ success: boolean; replyId: string }>('/social/forums/replies', data),
  likeContent: (userId: string, threadId?: string, replyId?: string) =>
    api.post<{ success: boolean }>('/social/forums/like', { userId, threadId, replyId }),

  // Achievements
  getAllAchievements: () =>
    api.get<{ success: boolean; achievements: any[] }>('/social/achievements'),
  getPlayerAchievements: (profileId: string) =>
    api.get<{ success: boolean; achievements: any[]; totalPoints: number }>(`/social/achievements/player/${profileId}`),

  // Leaderboards
  getLeaderboard: (type: string, params?: { organizationId?: string; timePeriod?: string; limit?: number }) =>
    api.get<{ success: boolean; leaderboard: any[] }>(`/social/leaderboards/${type}`, { params }),

  // Activity Feed
  getActivityFeed: (profileId: string, page?: number) =>
    api.get<{ success: boolean; activities: any[] }>(`/social/activity/${profileId}?page=${page || 1}`),

  // Milestones
  getMilestones: (profileId: string) =>
    api.get<{ success: boolean; milestones: any[] }>(`/social/milestones/${profileId}`),

  // Player Authentication & Verification
  checkPlayerAuth: (uscfId: string) =>
    api.get<{
      success: boolean;
      uscfId: string;
      playerName?: string;
      requiresVerification: boolean;
      hasEmail: boolean;
      hasPhone: boolean;
      emailHints: string[];
      phoneHints: string[];
      totalRegistrations: number;
    }>(`/social/auth/check/${uscfId}`),

  verifyPlayer: (uscfId: string, credential: string) =>
    api.post<{
      success: boolean;
      verified: boolean;
      error?: string;
      player?: {
        uscfId: string;
        name: string;
        email?: string;
        phone?: string;
        rating?: number;
      };
    }>('/social/auth/verify', { uscfId, credential }),

  // Sync USCF data to social profile
  syncUscfData: (uscfId: string) =>
    api.post<{
      success: boolean;
      profile: any;
    }>(`/social/sync-uscf/${uscfId}`)
};

// Organizer Analytics API
export const organizerAnalyticsApi = {
  // Dashboard
  getDashboard: (organizationId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get<{ success: boolean; dashboard: any }>(`/organizer-analytics/dashboard/${organizationId}`, { params }),

  // Revenue
  getRevenue: (organizationId: string, params?: { startDate?: string; endDate?: string; groupBy?: string }) =>
    api.get<{ success: boolean; byPeriod: any[]; byTournament: any[]; byType: any[] }>(`/organizer-analytics/revenue/${organizationId}`, { params }),
  addRevenue: (data: { organizationId: string; tournamentId?: string; recordType: string; amount: number; paymentMethod?: string; playerId?: string; playerName?: string; description?: string; recordedDate?: string }) =>
    api.post<{ success: boolean; recordId: string }>('/organizer-analytics/revenue', data),

  // Attendance
  getAttendance: (organizationId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get<{ success: boolean; byTournament: any[]; byMonth: any[]; ratingDistribution: any[] }>(`/organizer-analytics/attendance/${organizationId}`, { params }),

  // Retention
  getRetention: (organizationId: string) =>
    api.get<{ success: boolean; overview: any; cohortAnalysis: any[]; topPlayers: any[]; atRiskPlayers: any[] }>(`/organizer-analytics/retention/${organizationId}`),
  updateRetention: (organizationId: string) =>
    api.post<{ success: boolean; updated: number }>(`/organizer-analytics/retention/update/${organizationId}`),

  // Benchmarking
  getBenchmarks: (organizationId: string, params?: { period?: string }) =>
    api.get<{ success: boolean; organizationMetrics: any; industryBenchmarks: any; comparison: any }>(`/organizer-analytics/benchmarks/${organizationId}`, { params }),

  // Tournament Analytics
  createTournamentSnapshot: (tournamentId: string) =>
    api.post<{ success: boolean }>(`/organizer-analytics/tournaments/${tournamentId}/snapshot`),

  // Dashboard Configuration
  getDashboardConfig: (userId: string, organizationId?: string) =>
    api.get<{ success: boolean; config: any }>(`/organizer-analytics/dashboard-config/${userId}`, { params: { organizationId } }),
  saveDashboardConfig: (userId: string, config: { organizationId?: string; widgets: any[]; layout?: string; theme?: string }) =>
    api.put<{ success: boolean }>(`/organizer-analytics/dashboard-config/${userId}`, config),

  // Expenses
  getExpenses: (organizationId: string, params?: { startDate?: string; endDate?: string; tournamentId?: string }) =>
    api.get<{ success: boolean; expenses: any[]; summary: any[] }>(`/organizer-analytics/expenses/${organizationId}`, { params }),
  addExpense: (data: { organizationId: string; tournamentId?: string; expenseType: string; amount: number; vendor?: string; description?: string; receiptUrl?: string; expenseDate?: string }) =>
    api.post<{ success: boolean; recordId: string }>('/organizer-analytics/expenses', data)
};

// ─── Chatbot streaming (fetch + SSE; not axios — avoids buffering) ─────────────

export type ChatbotStreamDonePayload = {
  type: 'done';
  success: boolean;
  conversationId?: string;
  response: string;
  intent: string;
  suggestions?: string[];
  usage?: { input_tokens: number; output_tokens: number };
  month_usage?: { message_count: number; total_tokens: number; cost_cents: number };
  /** When set, client opens this tournament screen (same ids as header search). */
  navigation_id?: string;
  limits?: {
    messages_month: number;
    tokens_month: number;
    price_input_cents_per_1k: number;
    price_output_cents_per_1k: number;
    model: string;
    enterprise_plan_enforced?: boolean;
    public_promo_active?: boolean;
  };
};

type ChatbotStreamEvent =
  | { type: 'delta'; text: string }
  | ChatbotStreamDonePayload
  | { type: 'error'; error: string };

export async function streamChatbotMessage(
  message: string,
  opts: {
    tournamentId?: string;
    conversationId?: string;
    organizationId?: string;
    onDelta: (text: string) => void;
    onDone: (payload: ChatbotStreamDonePayload) => void;
    onError: (message: string) => void;
  }
): Promise<void> {
  const token = localStorage.getItem('token');
  const url = `${getApiBaseUrl()}/chatbot/message/stream`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      message,
      tournamentId: opts.tournamentId,
      conversationId: opts.conversationId,
      organizationId: opts.organizationId,
    }),
  });

  if (!res.ok) {
    let errMsg = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      if (typeof j?.error === 'string') errMsg = j.error;
    } catch {
      try {
        const t = await res.text();
        if (t) errMsg = t.slice(0, 200);
      } catch {
        /* ignore */
      }
    }
    opts.onError(errMsg);
    return;
  }

  if (!res.body) {
    opts.onError('No response body');
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawDone = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep;
      while ((sep = buffer.indexOf('\n\n')) >= 0) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        for (const line of raw.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          let payload: ChatbotStreamEvent;
          try {
            payload = JSON.parse(line.slice(6)) as ChatbotStreamEvent;
          } catch {
            opts.onError('Invalid server response');
            return;
          }
          if (payload.type === 'delta') opts.onDelta(payload.text);
          else if (payload.type === 'done') {
            sawDone = true;
            opts.onDone(payload);
          } else if (payload.type === 'error') {
            opts.onError(payload.error);
            return;
          }
        }
      }
    }
  } finally {
    reader.releaseLock?.();
  }

  if (buffer.trim()) {
    for (const line of buffer.split('\n')) {
      if (!line.startsWith('data: ')) continue;
      let payload: ChatbotStreamEvent;
      try {
        payload = JSON.parse(line.slice(6)) as ChatbotStreamEvent;
      } catch {
        opts.onError('Invalid server response');
        return;
      }
      if (payload.type === 'delta') opts.onDelta(payload.text);
      else if (payload.type === 'done') {
        sawDone = true;
        opts.onDone(payload);
      } else if (payload.type === 'error') {
        opts.onError(payload.error);
        return;
      }
    }
  }

  if (!sawDone) opts.onError('Connection closed before completion');
}

// Chatbot API
export const chatbotApi = {
  // Create a new conversation session (returns greeting message)
  createConversation: (tournamentId?: string, organizationId?: string) =>
    api.post<{ success: boolean; conversationId: string; greeting: string }>('/chatbot/conversations', { tournamentId, organizationId }),

  // Get current user's chatbot usage
  getUsage: () =>
    api.get<{
      success: boolean;
      current_month: string;
      current: {
        month?: string;
        message_count: number;
        input_tokens?: number;
        output_tokens?: number;
        total_tokens: number;
        cost_cents: number;
      };
      limits: {
        messages_month: number;
        tokens_month: number;
        price_input_cents_per_1k: number;
        price_output_cents_per_1k: number;
        model: string;
        enterprise_plan_enforced?: boolean;
        public_promo_active?: boolean;
      };
      history: any[];
    }>('/chatbot/usage'),

  /** Server chatbot flags (e.g. eval bypass). Requires auth. */
  getConfig: () =>
    api.get<{
      success: boolean;
      enterprise_plan_enforced: boolean;
      limits: {
        messages_month: number;
        tokens_month: number;
        price_input_cents_per_1k: number;
        price_output_cents_per_1k: number;
        model: string;
        enterprise_plan_enforced?: boolean;
        public_promo_active?: boolean;
      };
    }>('/chatbot/config'),

  /** Stream assistant reply via SSE (POST `/chatbot/message/stream`). */
  sendMessageStream: streamChatbotMessage,

  // Send a message and get a response
  sendMessage: (message: string, tournamentId?: string, conversationId?: string, organizationId?: string) =>
    api.post<{
      success: boolean;
      response: string;
      intent: string;
      conversationId?: string;
      requiresConfirmation?: boolean;
      action?: { type: string; method: string; url: string; body: any; label: string };
      data?: any;
      suggestions?: string[];
      month_usage?: { message_count: number; total_tokens: number; cost_cents: number };
      limits?: {
        messages_month: number;
        tokens_month: number;
        price_input_cents_per_1k: number;
        price_output_cents_per_1k: number;
        model: string;
      };
    }>('/chatbot/message', { message, tournamentId, conversationId, organizationId }),

  // Get conversation history
  getHistory: (conversationId: string) =>
    api.get<{ success: boolean; conversationId: string; messages: any[]; tournamentId: string }>(`/chatbot/conversations/${conversationId}/history`),

  // Get full tournament context summary
  getTournamentContext: (tournamentId: string) =>
    api.get<{ success: boolean; tournament: any; currentRound: number; totalRounds: any; totalPlayers: number; pendingResults: number; topStandings: any[] }>(`/chatbot/tournament/${tournamentId}/context`),

  // Get next recommended action
  getNextAction: (tournamentId: string) =>
    api.get<{ success: boolean; nextAction: string; description: string; command?: string; action?: any }>(`/chatbot/tournament/${tournamentId}/next-action`),

  // Org-scoped chatbot config (init the widget)
  getOrgChatbot: (orgSlug: string) =>
    api.get<{ success: boolean; chatbot: { organization: any; activeTournaments: any[]; greeting: string; capabilities: string[] } }>(`/chatbot/org/${orgSlug}`),

  // Org-scoped message
  sendOrgMessage: (orgSlug: string, message: string, tournamentId?: string, conversationId?: string) =>
    api.post<{ success: boolean; response: string; intent: string; action?: any; suggestions?: string[] }>(`/chatbot/org/${orgSlug}/message`, { message, tournamentId, conversationId }),
};

// Google Meet API (coach connects Google for training session video calls)
export const googleMeetApi = {
  getStatus: (params?: { coachId?: string }) => {
    const q = new URLSearchParams();
    if (params?.coachId) q.set('coachId', params.coachId);
    const qs = q.toString();
    return api.get<{
      success: boolean;
      data: { appConfigured: boolean; connected: boolean; email: string | null; isCurrentUser: boolean };
      error?: string;
    }>(`/google-meet/status${qs ? `?${qs}` : ''}`);
  },
  startOAuth: (organizationId?: string, redirectPath?: string) => {
    const q = new URLSearchParams();
    if (organizationId) q.set('organizationId', organizationId);
    if (redirectPath) q.set('redirectPath', redirectPath);
    const qs = q.toString();
    return api.get<{ success: boolean; authUrl: string; error?: string }>(
      `/google-meet/oauth/start${qs ? `?${qs}` : ''}`
    );
  },
  disconnect: () =>
    api.delete<{ success: boolean; message: string; error?: string }>('/google-meet/connect'),
};

// Collaborative analysis boards (training sessions + standalone)
export const analysisBoardApi = {
  getCoachOrgs: () =>
    api.get<{ success: boolean; data: { orgs: { id: string; name: string; slug: string; coachMemberId: string | null }[] }; error?: string }>('/boards/coach-orgs'),
  getByCode: (boardCode: string) =>
    api.get<{ success: boolean; data: { board: any }; error?: string }>(`/boards/${encodeURIComponent(boardCode)}`),
  list: (organizationId: string) =>
    api.get<{ success: boolean; data: { boards: any[] }; error?: string }>(
      `/boards?organizationId=${encodeURIComponent(organizationId)}`
    ),
  create: (data: {
    organizationId?: string;
    coachId?: string;
    title: string;
    description?: string;
    initialFen?: string;
  }) =>
    api.post<{
      success: boolean;
      message: string;
      data: { id: string; boardCode: string; title: string; sharePath: string };
      error?: string;
    }>('/boards', data),
  saveState: (boardCode: string, data: { currentFen?: string; moveHistory?: unknown[]; annotations?: unknown[] }) =>
    api.put<{ success: boolean; error?: string }>(`/boards/${encodeURIComponent(boardCode)}/state`, data),
  getVideoIntegrations: (boardCode: string) =>
    api.get<{
      success: boolean;
      data?: {
        zoom: { appConfigured: boolean; connected: boolean; email: string | null };
        googleMeet: { appConfigured: boolean; connected: boolean; email: string | null };
        stored: {
          zoomJoinUrl?: string | null;
          zoomStartUrl?: string | null;
          googleMeetJoinUrl?: string | null;
        };
      };
      error?: string;
      code?: string;
    }>(`/boards/${encodeURIComponent(boardCode)}/video-integrations`),
  createZoomMeeting: (boardCode: string) =>
    api.post<{
      success: boolean;
      data?: {
        reused: boolean;
        joinUrl: string;
        startUrl: string;
        password: string | null;
      };
      error?: string;
      code?: string;
    }>(`/boards/${encodeURIComponent(boardCode)}/zoom-meeting`),
  createGoogleMeet: (boardCode: string) =>
    api.post<{
      success: boolean;
      data?: { joinUrl: string; startUrl: string };
      error?: string;
      code?: string;
    }>(`/boards/${encodeURIComponent(boardCode)}/google-meet`),
};

export const zoomApi = {
  getStatus: (params?: { coachId?: string }) => {
    const q = new URLSearchParams();
    if (params?.coachId) q.set('coachId', params.coachId);
    const qs = q.toString();
    return api.get<{
      success: boolean;
      data: { appConfigured: boolean; connected: boolean; email: string | null; isCurrentUser: boolean };
      error?: string;
    }>(`/zoom/status${qs ? `?${qs}` : ''}`);
  },
  startOAuth: (organizationId?: string, redirectPath?: string) => {
    const q = new URLSearchParams();
    if (organizationId) q.set('organizationId', organizationId);
    if (redirectPath) q.set('redirectPath', redirectPath);
    const qs = q.toString();
    return api.get<{ success: boolean; authUrl: string; error?: string }>(
      `/zoom/oauth/start${qs ? `?${qs}` : ''}`
    );
  },
  disconnect: () =>
    api.delete<{ success: boolean; message: string; error?: string }>('/zoom/connect'),
};

// Training Sessions API
export const trainingSessionApi = {
  getAll: (organizationId: string, params?: { status?: string; coachId?: string }) => {
    const queryParams = new URLSearchParams({ organizationId });
    if (params?.status) queryParams.append('status', params.status);
    if (params?.coachId) queryParams.append('coachId', params.coachId);
    return api.get<{ success: boolean; data: { sessions: any[] }; error?: string }>(`/training-sessions?${queryParams.toString()}`);
  },
  getById: (id: string) =>
    api.get<{ success: boolean; data: { session: any }; error?: string }>(`/training-sessions/${id}`),
  getByRoomCode: (roomCode: string) =>
    api.get<{ success: boolean; data: { session: any }; error?: string }>(`/training-sessions/room/${roomCode}`),
  create: (data: {
    organizationId: string; coachId: string; title: string;
    description?: string; scheduledAt?: string; durationMinutes?: number;
    initialFen?: string; studentIds?: string[]; createZoomMeeting?: boolean;
    createLogiqBoard?: boolean; customAnalysisBoardUrl?: string;
  }) =>
    api.post<{
      success: boolean; message: string;
      data: {
        id: string; roomCode: string; status: string;
        zoomJoinUrl?: string | null; zoomStartUrl?: string | null; zoomWarning?: string | null;
        customAnalysisBoardUrl?: string | null; analysisBoardUrl?: string | null;
        logiqBoardUrl?: string | null; logiqBoardStatus?: string | null; logiqBoardWarning?: string | null;
      };
      error?: string;
    }>('/training-sessions', data),
  createBoard: (id: string) =>
    api.post<{ success: boolean; message: string; data: { logiqBoardStatus: string }; error?: string }>(
      `/training-sessions/${id}/create-board`
    ),
  saveNotes: (id: string, coachNotes: string) =>
    api.put<{ success: boolean; message: string; error?: string }>(`/training-sessions/${id}/notes`, { coachNotes }),
  saveAttendance: (id: string, records: Array<{ studentId: string; attended: boolean }>) =>
    api.post<{ success: boolean; message: string; data: { updated: number }; error?: string }>(
      `/training-sessions/${id}/attendance`, { records }
    ),
  assignHomework: (
    id: string,
    data: {
      tacticIds?: string[];
      lichessPuzzles?: { rating?: number; theme?: string; count?: number };
    }
  ) =>
    api.post<{
      success: boolean;
      message: string;
      data?: { tacticsAssigned: number; puzzlesAssigned: number; studentCount: number };
      error?: string;
    }>(`/training-sessions/${id}/assign-homework`, data),
  update: (id: string, data: any) =>
    api.put<{ success: boolean; message: string; error?: string }>(`/training-sessions/${id}`, data),
  start: (id: string) =>
    api.post<{ success: boolean; message: string; data: { roomCode: string; status: string }; error?: string }>(`/training-sessions/${id}/start`),
  end: (id: string) =>
    api.post<{ success: boolean; message: string; error?: string }>(`/training-sessions/${id}/end`),
  delete: (id: string) =>
    api.delete<{ success: boolean; message: string; error?: string }>(`/training-sessions/${id}`),
  updateBoard: (id: string, data: { currentFen?: string; moveHistory?: string[]; annotations?: string[] }) =>
    api.put<{ success: boolean; error?: string }>(`/training-sessions/${id}/board`, data),
  getStudentSessions: (studentId: string) =>
    api.get<{ success: boolean; data: { sessions: any[] }; error?: string }>(
      `/training-sessions/student/${encodeURIComponent(studentId)}`
    ),
  getMySessions: () =>
    api.get<{
      success: boolean;
      data: { sessions: any[]; memberIds?: string[] };
      error?: string;
    }>('/training-sessions/for-me'),
};

// Tactics API
export const tacticsApi = {
  getAll: (organizationId: string, sessionId?: string) => {
    const params = new URLSearchParams({ organizationId });
    if (sessionId) params.append('sessionId', sessionId);
    return api.get<{ success: boolean; data: { tactics: any[] }; error?: string }>(`/tactics?${params.toString()}`);
  },
  getById: (id: string) =>
    api.get<{ success: boolean; data: { tactic: any }; error?: string }>(`/tactics/${id}`),
  create: (data: {
    organizationId: string; sessionId?: string; title: string;
    description?: string; initialFen: string; solutionMoves: { from: string; to: string; promotion?: string }[];
    difficulty?: string; studentIds?: string[];
  }) =>
    api.post<{ success: boolean; message: string; data: { id: string }; error?: string }>('/tactics', data),
  delete: (id: string) =>
    api.delete<{ success: boolean; message: string; error?: string }>(`/tactics/${id}`),
  attempt: (id: string, studentId: string, moves: { from: string; to: string }[]) =>
    api.post<{ success: boolean; data: { correct: boolean; message: string }; error?: string }>(`/tactics/${id}/attempt`, { studentId, moves }),
  assign: (id: string, studentIds: string[]) =>
    api.post<{ success: boolean; message: string; error?: string }>(`/tactics/${id}/assign`, { studentIds }),
  unassign: (id: string, studentId: string) =>
    api.delete<{ success: boolean; message: string; error?: string }>(`/tactics/${id}/assign/${studentId}`),
  getStudentTactics: (studentId: string) =>
    api.get<{ success: boolean; data: { tactics: any[] }; error?: string }>(`/tactics/student/${studentId}`),
  fetchLichessPuzzles: (rating: number, theme: string, count: number) =>
    api.get<{ success: boolean; data: { puzzles: any[]; count: number }; error?: string }>(
      `/tactics/fetch-lichess-puzzles?rating=${rating}&theme=${encodeURIComponent(theme)}&count=${count}`
    ),
  bulkCreateAssign: (organizationId: string, puzzles: any[], studentIds: string[]) =>
    api.post<{ success: boolean; data: { created: number; message: string }; error?: string }>(
      '/tactics/bulk-create-assign',
      { organizationId, puzzles, studentIds }
    ),
};

export const puzzleAssignmentsApi = {
  fetch: (rating: number, theme: string, count: number) =>
    api.get<{ success: boolean; data: { puzzles: any[]; count: number }; error?: string }>(
      `/puzzle-assignments/fetch?rating=${rating}&theme=${encodeURIComponent(theme)}&count=${count}`
    ),
  create: (data: { organizationId: string; title?: string; rating?: number; theme?: string; puzzles: any[]; studentIds: string[] }) =>
    api.post<{ success: boolean; data: { id: string; message: string }; error?: string }>('/puzzle-assignments', data),
  getAll: (organizationId: string) =>
    api.get<{ success: boolean; data: { assignments: any[] }; error?: string }>(`/puzzle-assignments?organizationId=${organizationId}`),
  getById: (id: string) =>
    api.get<{ success: boolean; data: { assignment: any; students: any[] }; error?: string }>(`/puzzle-assignments/${id}`),
  getStudentAssignments: (studentId: string) =>
    api.get<{ success: boolean; data: { assignments: any[] }; error?: string }>(`/puzzle-assignments/student/${studentId}`),
  saveProgress: (id: string, studentId: string, currentIndex: number, puzzleResult?: { puzzleId: string; solved: boolean; attempts: number }) =>
    api.post<{ success: boolean; data: { currentIndex: number; status: string; results: any[] }; error?: string }>(
      `/puzzle-assignments/${id}/progress`,
      { studentId, currentIndex, puzzleResult }
    ),
  delete: (id: string) =>
    api.delete<{ success: boolean; message: string; error?: string }>(`/puzzle-assignments/${id}`),
};

export const shadowBotApi = {
  build: (params: {
    username: string;
    platform: 'lichess' | 'chesscom';
    color?: 'white' | 'black' | '';
    maxGames?: number;
    perfType?: string;
  }) =>
    api.get<{
      success: boolean;
      data: {
        book: Record<string, { uci: string; san: string; count: number }[]>;
        stats: {
          analyzedCount: number;
          totalGamePlays: number | null;
          avgRating: number | null;
          topOpenings: { name: string; count: number; pct: number }[];
          totalPositions: number;
          source: 'explorer' | 'games';
        };
        username: string;
        platform: string;
        color: string;
      };
      error?: string;
    }>('/shadowbot/build', { params }),
};

export { api };
export default api;
